import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { FiasBridgeContext } from './context';
import { FiasBridgeError, getBridge } from './bridge';
import { ENTITY_INVOCATION_REQUEST_LIMITS } from './invocation-params';
import type {
  ArcheHandoffPayload,
  FiasUser,
  FiasTheme,
  FiasStorageApi,
  FiasWebSourceDisagreedEntries,
  DataSubscriptionState,
  EntitySubscriptionState,
  EntityInvocationApi,
  EntityInvocationOptions,
  EntityInvocationParams,
  EntityInvocationResult,
  FiasTextModel,
  FiasHolder,
  ClientEntityApi,
  ImageGenerationApi,
  ImageGenerationParams,
  ImageGenerationResult,
  ImageEntityFilter,
  ImageEntitySummary,
  NllbLanguageOption,
  AudioGenerationApi,
  AudioGenerationParams,
  AudioGenerationResult,
  FiasNavigationApi,
  ArcheHandoffPayloadInput,
  StepNavigationApi,
  StepNavigationOptions,
  FiasDataStoreApi,
  DataStoreCollection,
  DataStoreWritePolicy,
  DataStoreCollectionConstraints,
  DataStoreBatchOptions,
  DataStoreBatchResult,
  DataStorePutResult,
  DataStoreDocument,
  DataStoreAttestation,
  DataStoreQueryOptions,
  DataStoreQueryResult,
  DataStoreSearchOptions,
  DataStoreSearchMatch,
  DataStoreScopeOptions,
  DataStoreBatchOp,
  FiasWorkspacesApi,
  FiasAccess,
  FiasAccessState,
  DataStoreWorkspace,
  DataStoreWorkspaceMember,
  FiasPerson,
  AddedWorkspacePerson,
  WorkspaceMemberTarget,
  WorkspaceRole,
  FiasStoreApi,
  StoreProduct,
  StoreEntitlement,
  StorePurchase,
  StorePurchaseResult,
  StorePurchaseOptions,
  VaultDocumentsApi,
  VaultDocumentsListOptions,
  VaultDocumentsListResult,
  VaultDocumentDownloadUrl,
  VaultDocumentWriteParams,
  VaultDocumentUpdateParams,
  VaultDocumentSaveContentParams,
  VaultDocumentSaveContentResult,
  VaultUserDocumentSaveContentResult,
  VaultDocumentSearchOptions,
  VaultDocumentSearchResult,
  VaultDocumentReferenceTargetType,
  VaultDocumentReferenceResult,
  VaultDocumentSaveCopyOptions,
  VaultDocumentUploadParams,
  VaultDocumentUploadInitResult,
  VaultDocumentUploadResult,
  ArcheAssetsApi,
  FiasConnection,
  FiasConnectResult,
  FiasConnectionsApi,
  FiasWebSourceData,
  FiasWebSourceExtractRequest,
  FiasWebSourceExtraction,
  FiasWebSourceListOptions,
  FiasWebSourceSiteList,
  FiasWebSourcesApi,
  FiasCreativeItem,
  FiasCreativeLibraryApi,
  FiasCreativePickResult,
  FiasXApi,
  FiasXPublishResult,
  FiasXUser,
  FiasXPost,
  FiasXPostMetrics,
  FiasXAccountStats,
  FiasLinkedInApi,
  FiasSchedulesApi,
  FiasSchedule,
  FiasLinkedInOrganization,
  ArcheAssetBytesApi,
  ArcheAssetBytes,
  ArcheAssetRef,
  ArcheAssetErrorCode,
  FiasContentApi,
  FiasContentErrorCode,
  FiasContentFile,
  FiasContentUrl,
  FiasLocalStoreApi,
  FiasLocalStoreErrorCode,
  FiasLocalStoreEntry,
  FiasLocalStoreEntryMeta,
  FiasLocalStoreEstimate,
  FiasLocalStoreValue,
  ArcheAssetsListOptions,
  ArcheAssetsListResult,
  ArcheAssetsIndexOptions,
  ArcheAssetsIndexResult,
  ArcheLibraryAsset,
  CommunityAsset,
  CommunityAssetUrl,
  CommunityAssetsApi,
  CommunityAssetPublishInput,
  CommunityAssetPublishResult,
  CommunityAssetListOptions,
  CommunityAssetListResult,
  CommunityAssetReportReason,
  CompanyEmailApi,
  CompanyEmailSendParams,
  CompanyEmailSendResult,
  FiasAIActionsDeclaration,
  FiasChromeApi,
  FiasOpener,
  FiasOpenerApi,
  FiasSendToOpenerResult,
  FiasChromeState,
} from './types';
import type { FiasBridge } from './bridge';
import {
  invokeEntityOp,
  invokeStoreOp,
  PLUGIN_STORAGE_ENTITY_ID,
  PLUGIN_DATASTORE_ENTITY_ID,
  PLUGIN_WORKSPACES_ENTITY_ID,
  APP_ACCESS_DIALOG_ENTITY_ID,
  WORKSPACE_ADD_PEOPLE_ENTITY_ID,
  CREATIVE_PICKER_ENTITY_ID,
  SEND_TO_OPENER_ENTITY_ID,
  VAULT_USER_DOCUMENTS_ENTITY_ID,
  DOCUMENT_SIGN_ENTITY_ID,
  VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
  COMMUNITY_ASSETS_ENTITY_ID,
  COMPANY_EMAIL_SEND_ENTITY_ID,
  CONNECTIONS_ENTITY_ID,
  VAULT_CREATIVE_ENTITY_ID,
  CONNECTIONS_CONNECT_ENTITY_ID,
  WEB_SOURCES_ENTITY_ID,
  X_ENTITY_ID,
  LINKEDIN_ENTITY_ID,
  SCHEDULED_INVOCATIONS_ENTITY_ID,
  COMMUNITY_ASSET_MAX_URL_BATCH,
  COMMUNITY_ASSET_MAX_PUBLISH_BATCH,
} from './entity-ops';

/**
 * Internal helper to get bridge from context, throws if not wrapped in FiasProvider.
 */
function useBridge(): FiasBridge {
  const bridge = useContext(FiasBridgeContext);
  if (!bridge) {
    throw new Error('useBridge must be used within a <FiasProvider>');
  }
  return bridge;
}

/**
 * Trailing-edge debounce window for persistent-state writes. Rapid setter
 * calls (e.g. per-frame updates from a requestAnimationFrame game loop) are
 * coalesced so we don't trip the bridge's 50-in-5s runaway-loop detector or
 * exhaust the 120/minute storage_write budget.
 */
const PERSIST_DEBOUNCE_WAIT_MS = 250;
const PERSIST_DEBOUNCE_MAX_WAIT_MS = 1000;

interface PendingWrite {
  path: string;
  content: string;
  timerId: ReturnType<typeof setTimeout> | null;
  firstScheduledAt: number;
}

interface DebouncedWriter {
  schedule(path: string, content: string): void;
  flush(): void;
}

/**
 * Create a debounced storage_write scheduler. Writes are coalesced to the
 * last value seen within a 250ms idle window, capped at a 1s max-wait so
 * continuous updates still persist at least once per second. Call `flush()`
 * on unmount or before switching keys to avoid losing the pending value.
 *
 * If `schedule` is called with a different `path` than the currently pending
 * write, the pending write flushes synchronously before the new one is
 * queued — each path gets a write.
 */
function createDebouncedWriter(bridge: FiasBridge): DebouncedWriter {
  let pending: PendingWrite | null = null;

  function flush(): void {
    if (!pending) return;
    const { path, content, timerId } = pending;
    if (timerId !== null) clearTimeout(timerId);
    pending = null;
    invokeEntityOp<void>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'write', { path, content }).catch(
      () => {},
    );
  }

  function schedule(path: string, content: string): void {
    const now = Date.now();
    if (pending && pending.path !== path) {
      flush();
    }
    if (!pending) {
      pending = { path, content, timerId: null, firstScheduledAt: now };
    } else {
      pending.content = content;
    }
    if (pending.timerId !== null) clearTimeout(pending.timerId);
    const elapsed = now - pending.firstScheduledAt;
    const remainingMax = Math.max(0, PERSIST_DEBOUNCE_MAX_WAIT_MS - elapsed);
    const delay = Math.min(PERSIST_DEBOUNCE_WAIT_MS, remainingMax);
    pending.timerId = setTimeout(flush, delay);
  }

  return { schedule, flush };
}

/** The platform's `resolve_people` limit per call; `resolvePeople` pages above it. */
const RESOLVE_PEOPLE_PAGE = 100;

/**
 * Access the current user's profile information.
 * Requires the `user:profile:read` permission in fias-plugin.json.
 *
 * If the `get_user` bridge request fails — most commonly because the plugin
 * called this hook without declaring `user:profile:read` — the error is
 * thrown during render so it surfaces to the nearest error boundary
 * (`FiasProvider` installs one). Previously a failed/denied request left the
 * hook permanently returning `null`, indistinguishable from "still loading",
 * so a UI that gated on `if (!user)` hung on an infinite spinner forever.
 *
 * @returns The current user's profile or null while loading.
 */

export function useFiasUser(): FiasUser | null {
  const bridge = useBridge();
  const [user, setUser] = useState<FiasUser | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    bridge.request<FiasUser>('get_user', {}).then(
      (data) => {
        if (mounted) setUser(data);
      },
      (err: unknown) => {
        if (mounted) setError(err instanceof Error ? err : new Error(String(err)));
      },
    );
    return () => {
      mounted = false;
    };
  }, [bridge]);

  if (error) throw error;

  return user;
}

/**
 * Access platform theme tokens for consistent styling.
 * Automatically updates when the platform theme changes (e.g., light/dark mode).
 * Requires the `theme:read` permission in fias-plugin.json.
 *
 * @returns The current theme tokens or null while loading.
 */
export function useFiasTheme(): FiasTheme | null {
  const bridge = useBridge();
  const [theme, setTheme] = useState<FiasTheme | null>(bridge.getTheme());

  useEffect(() => {
    return bridge.onThemeUpdate(setTheme);
  }, [bridge]);

  return theme;
}

/**
 * Self-hosted asset URLs for a platform-vendored library, for plugins that
 * declare the `sandbox:vendored-libraries` permission. Returns a file-key → URL
 * map (for `pdfjs-dist`: `{ mainModule, worker, cMapUrl, standardFontDataUrl }`),
 * or `null` when the host's CDN is unconfigured or the library isn't vendored
 * (the plugin should then degrade gracefully).
 *
 * Delivered in the init payload (no gated request); values are static for the
 * session. The import specifier is already pinned to its main module via the
 * host importmap, so a plugin typically only needs `worker` (pass to the
 * pdf-core loader's `workerModuleUrl`) and the CMap/font directories.
 *
 * @param importSpecifier The vendored library's bare module name (e.g. `'pdfjs-dist'`).
 * @returns The library's asset URLs, or null if unavailable.
 */
export function useFiasVendoredAssets(importSpecifier: string): Record<string, string> | null {
  const bridge = useBridge();
  return bridge.getVendoredAssets(importSpecifier);
}

/**
 * Access the plugin's sandboxed storage.
 * Requires the `storage:sandbox` permission in fias-plugin.json.
 *
 * @returns Storage API methods (readFile, writeFile, listFiles, deleteFile).
 */
export function useFiasStorage(): FiasStorageApi {
  const bridge = useBridge();

  const readFile = useCallback(
    async (path: string): Promise<string | null> => {
      const res = await invokeEntityOp<{ content: string | null; exists: boolean }>(
        bridge,
        PLUGIN_STORAGE_ENTITY_ID,
        'read',
        { path },
      );
      return res?.content ?? null;
    },
    [bridge],
  );

  const writeFile = useCallback(
    async (path: string, content: string): Promise<void> => {
      await invokeEntityOp<{ success: boolean }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'write', {
        path,
        content,
      });
    },
    [bridge],
  );

  const listFiles = useCallback(
    async (prefix?: string): Promise<string[]> => {
      const res = await invokeEntityOp<{ files: string[] }>(
        bridge,
        PLUGIN_STORAGE_ENTITY_ID,
        'list',
        { prefix },
      );
      return res?.files ?? [];
    },
    [bridge],
  );

  const deleteFile = useCallback(
    async (path: string): Promise<void> => {
      await invokeEntityOp<{ success: boolean }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'delete', {
        path,
      });
    },
    [bridge],
  );

  // Memoized, like useArcheAssets. An unmemoized object literal here is a
  // footgun rather than a style question: the natural way to use this hook is
  // `useEffect(() => { void storage.readFile(...) }, [storage])`, and a fresh
  // identity every render turns that into an infinite loop — effect, setState,
  // render, effect. The inner callbacks were already stable; only the
  // container was not.
  return useMemo(
    () => ({ readFile, writeFile, listFiles, deleteFile }),
    [readFile, writeFile, listFiles, deleteFile],
  );
}

/**
 * Invoke platform entities (AI models, etc.) through the bridge.
 * Requires the `entities:invoke` permission in fias-plugin.json.
 *
 * Rendering pattern: `streamingText` accumulates tokens during the call AND
 * keeps the full text after `isLoading` flips to `false` — it is not cleared
 * when the response completes. `result.output` also holds the full text once
 * the promise resolves. **Render only one slot** (e.g.
 * `{isLoading ? streamingText : result?.output}`) — rendering both prints the
 * response twice once the stream completes.
 *
 * Concurrent calls: pass `onToken` (and optionally `onThinking`) as the second
 * argument to `invoke`. Those callbacks are scoped to that one call, and a call
 * that supplies `onToken` leaves the shared `streamingText` / `result` / `error`
 * alone (its failure arrives as the rejected promise),
 * so several streams can render side by side. `isLoading` is true while ANY
 * call is in flight.
 *
 * @returns Entity invocation API (invoke, isLoading, result, error, streamingText).
 */
export function useEntityInvocation(): EntityInvocationApi {
  const bridge = useBridge();
  const [inFlight, setInFlight] = useState(0);
  const [result, setResult] = useState<EntityInvocationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [streamingText, setStreamingText] = useState('');

  const invoke = useCallback(
    async (
      params: EntityInvocationParams,
      options?: EntityInvocationOptions,
    ): Promise<EntityInvocationResult> => {
      // A call with its own onToken owns its output; the shared slots stay
      // with calls that rely on them, so concurrent streams never interleave.
      const usesSharedSlots = !options?.onToken;
      setInFlight((n) => n + 1);
      if (usesSharedSlots) {
        setError(null);
        setStreamingText('');
      }

      try {
        const res = await bridge.requestStream(params, {
          onToken: (token) => {
            if (options?.onToken) {
              options.onToken(token);
              return;
            }
            // flushSync forces React to render each token immediately.
            // Without it, React batches rapid stream_token messages into
            // a single render, making all text appear at once.
            flushSync(() => {
              setStreamingText((prev) => prev + token);
            });
          },
          onThinking: options?.onThinking,
          signal: options?.signal,
        });
        if (usesSharedSlots) setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        // A call with its own callbacks gets its failure from the rejection;
        // stopping one of several concurrent streams must not flag the rest.
        if (usesSharedSlots) setError(e);
        throw e;
      } finally {
        setInFlight((n) => n - 1);
      }
    },
    [bridge],
  );

  return { invoke, isLoading: inFlight > 0, result, error, streamingText };
}

/**
 * The platform's live catalog of text models this plugin can invoke — for a
 * model picker. Each entry's `entityId` goes straight into
 * `useEntityInvocation().invoke({ entityId, ... })`; `capabilities` says which
 * models accept images and `parameters.reasoning`; `pricing` is what the user
 * pays (credits per 1M tokens, markup included) for a cost label.
 *
 * Requires the `entities:invoke` permission. Returns `[]` until the host has
 * delivered the catalog (usually immediately), then re-renders with it.
 * Retired models drop out, so a saved model choice may disappear: fall back to
 * a `recommended` entry when it does.
 */
export function useTextModels(): FiasTextModel[] {
  const bridge = useBridge();
  const [models, setModels] = useState<FiasTextModel[]>(bridge.getTextModels() ?? []);

  useEffect(() => {
    // The catalog may have arrived between the initial render and this effect.
    setModels(bridge.getTextModels() ?? []);
    return bridge.onTextModelsUpdate(setModels);
  }, [bridge]);

  return models;
}

/**
 * The Fias workspace the plugin was opened in: the user's own (`personal`) or
 * an organization's (`org`), and the user's role there.
 *
 * Requires `user:profile:read` or `data:workspace`. Returns `null` until the
 * host has delivered it (usually immediately), and when the host could not
 * resolve it. The value never changes while the plugin is open — switching
 * workspace reopens the plugin. It carries no id and no name, and the
 * platform applies the workspace to every call itself.
 */
export function useFiasHolder(): FiasHolder | null {
  const bridge = useBridge();
  const [holder, setHolder] = useState<FiasHolder | null>(bridge.getHolder());

  useEffect(() => {
    // Init may have arrived between the initial render and this effect.
    setHolder(bridge.getHolder());
    return bridge.onHolderReady(setHolder);
  }, [bridge]);

  return holder;
}

/**
 * How long `generate` waits for an image. High-quality renders at large sizes
 * run past two minutes, and the platform keeps working on them for up to
 * 300 s. This is the outermost deadline — above the host's (310 s) — so a
 * slow image is delivered and a stuck one is reported by the platform with a
 * real reason, never cut off here after it was already charged.
 */
const IMAGE_GENERATE_TIMEOUT_MS = 320_000;

/**
 * Generate images using platform AI image models.
 *
 * Images are saved to the user's Fias file system and a presigned URL
 * is returned for display. Most images take 10-60 seconds; high quality at
 * large sizes can take several minutes, so the call waits up to
 * `IMAGE_GENERATE_TIMEOUT_MS`.
 *
 * @deprecated Phase 3 of the Invocation Surface Pattern migrates image
 * generation onto the registry. Prefer the generated typed hook
 * (`useImageGenerate()` from `@fias/arche-sdk` once the image surface
 * registers) over this hand-written one. The legacy `image_generate`
 * bridge op continues to work through the deprecation window. Tracking:
 * docs/architecture/invocation-surface-pattern.md §7 Phase 3.
 *
 * @returns Image generation API (generate, isLoading, result, error).
 */
export function useImageGeneration(): ImageGenerationApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ImageGenerationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (params: ImageGenerationParams): Promise<ImageGenerationResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await bridge.request<ImageGenerationResult>(
          'image_generate',
          params,
          IMAGE_GENERATE_TIMEOUT_MS,
        );
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { generate, isLoading, result, error };
}

/**
 * Discover image-generation entities by capability.
 *
 * Use when the plugin wants to render a picker (e.g., "let the user choose
 * which model to use"). For the common case of "just give me something
 * good," pass a selector directly to `useImageGeneration` instead —
 * `{ entityId: { platformRecommended: { mode: 'text-to-image' } }, ... }`
 * — and skip the discovery round-trip entirely.
 *
 * The filter is shallow-compared by JSON serialization. If you build a new
 * filter object on every render (`useImageEntities({ modes: ['text-to-image'] })`),
 * the equality check still avoids refetches — but pass a stable reference if
 * you can.
 *
 * @param filter Capability filter. Omit to list every published image entity.
 *
 * @example
 *   const { entities, isLoading } = useImageEntities({
 *     modes: ['image-to-image'],
 *     supportsReferenceImage: true,
 *   });
 */
export function useImageEntities(filter?: ImageEntityFilter): {
  entities: ImageEntitySummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const bridge = useBridge();
  const [entities, setEntities] = useState<ImageEntitySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize the filter so changing-but-equivalent object refs don't
  // trigger refetches.
  const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);

  const fetchOnce = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await bridge.request<{ entities: ImageEntitySummary[] }>(
        'entities_list_image',
        { filter: JSON.parse(filterKey) as ImageEntityFilter },
        30_000,
      );
      setEntities(res.entities ?? []);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [bridge, filterKey]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return { entities, isLoading, error, refetch: fetchOnce };
}

/**
 * `useBackgroundRemoval()` was REMOVED. It targeted the on-device imgly
 * background-removal entity, which was retired platform-wide for
 * licensing reasons (AGPL-3.0), along with its
 * `entities:image_remove_background` permission.
 *
 * There is no client-side replacement. Server-side background removal is
 * available to plugins through the paid `image.smart-remove-background`
 * invocation surface (Stability / Recraft) under the
 * `entities:image_edit` permission.
 */

/**
 * Default timeout for a client-side entity invocation. Client-side entities
 * (WASM models, dictionary engines) can take many seconds to warm up and
 * run, so we use the same 2-minute ceiling as the image/audio surfaces.
 */
const CLIENT_ENTITY_INVOKE_TIMEOUT_MS = 120_000;

/**
 * Whether a `client_entity_invoke` failed because the host has no executor
 * for the entity (an older Fias, or the dev harness): the capability is simply
 * not available there. Hosts answer `CLIENT_ENTITY_UNAVAILABLE`; older ones
 * only said so in the message ("no client-side executor …"), which is still
 * recognised. Any other bridge error is the app's to fix.
 */
function isClientEntityUnavailable(err: unknown): boolean {
  if (!(err instanceof FiasBridgeError)) return false;
  if (err.code === 'CLIENT_ENTITY_UNAVAILABLE') return true;
  return err.code === 'BRIDGE_ERROR' && err.message.includes('no client-side executor');
}

/**
 * Invoke a client-side ("on-device") entity through the bridge's generic
 * `client_entity_invoke` op. Client-side entities execute entirely in the
 * browser (e.g. the nspell + retext grammar checker, the NLLB-200
 * translator) — input never leaves the device and there is no per-call
 * credit cost.
 *
 * This is the client-side counterpart to {@link useEntityInvocation}, which
 * invokes server-side, credit-billed AI entities. Target an entity by its ID
 * and supply the input shape that entity documents; the typed result flows
 * back through the returned promise:
 *
 * ```tsx
 * const { invoke, isLoading, error } = useClientEntity();
 * const { issues } = await invoke<GrammarCheckInput, GrammarCheckResult>(
 *   GRAMMAR_CHECK_ENTITY_ID,
 *   { text },
 * );
 * ```
 *
 * Requires the `entities:client_invoke` permission in `fias-plugin.json`.
 * Per-entity rate limits (each entity's `pluginInvocable.rateLimitPerMinute`)
 * are enforced by the host.
 *
 * Input limits are enforced **host-side by each entity's executor**, not by
 * this hook — being generic, it cannot know a given entity's caps (e.g. the
 * grammar entity's 100,000-character limit). Oversized/invalid input is
 * rejected after it crosses the bridge, surfacing as a rejected promise /
 * `error`. If you want to fail fast before the round-trip, pre-validate in
 * your own code against the limits the entity documents.
 */
export function useClientEntity(): ClientEntityApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const invoke = useCallback(
    async <TInput = unknown, TResult = unknown>(
      entityId: string,
      input: TInput,
    ): Promise<TResult> => {
      setIsLoading(true);
      setError(null);

      try {
        return await bridge.request<TResult>(
          'client_entity_invoke',
          { entityId, input },
          CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
        );
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { invoke, isLoading, error };
}

/**
 * Entity ID of the NLLB-200 on-device translation entity. Exported so plugins
 * can target it via the generic {@link useClientEntity} hook, with
 * `TranslateParams` / `TranslationResult` as the I/O types.
 *
 * SYNC NOTE: must match `NLLB_TRANSLATION_ENTITY_ID` in
 * `packages/entities/src/integrations/nllb-translation/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this —
 * drift fails CI.
 */
export const NLLB_TRANSLATION_ENTITY_ID = 'ent_intg_nllb_xlate_000000000000';

/**
 * Curated list of target languages for the NLLB translator, as
 * label/FLORES-200-code pairs. Not exhaustive (NLLB supports 200 languages) —
 * a convenient default set for building a picker. Sorted by label.
 */
export const NLLB_LANGUAGES: readonly NllbLanguageOption[] = [
  { label: 'Arabic', code: 'arb_Arab' },
  { label: 'Bengali', code: 'ben_Beng' },
  { label: 'Bulgarian', code: 'bul_Cyrl' },
  { label: 'Catalan', code: 'cat_Latn' },
  { label: 'Chinese (Simplified)', code: 'zho_Hans' },
  { label: 'Chinese (Traditional)', code: 'zho_Hant' },
  { label: 'Croatian', code: 'hrv_Latn' },
  { label: 'Czech', code: 'ces_Latn' },
  { label: 'Danish', code: 'dan_Latn' },
  { label: 'Dutch', code: 'nld_Latn' },
  { label: 'English', code: 'eng_Latn' },
  { label: 'Finnish', code: 'fin_Latn' },
  { label: 'French', code: 'fra_Latn' },
  { label: 'German', code: 'deu_Latn' },
  { label: 'Greek', code: 'ell_Grek' },
  { label: 'Hebrew', code: 'heb_Hebr' },
  { label: 'Hindi', code: 'hin_Deva' },
  { label: 'Hungarian', code: 'hun_Latn' },
  { label: 'Indonesian', code: 'ind_Latn' },
  { label: 'Italian', code: 'ita_Latn' },
  { label: 'Japanese', code: 'jpn_Jpan' },
  { label: 'Korean', code: 'kor_Hang' },
  { label: 'Norwegian', code: 'nob_Latn' },
  { label: 'Persian', code: 'pes_Arab' },
  { label: 'Polish', code: 'pol_Latn' },
  { label: 'Portuguese', code: 'por_Latn' },
  { label: 'Romanian', code: 'ron_Latn' },
  { label: 'Russian', code: 'rus_Cyrl' },
  { label: 'Serbian', code: 'srp_Cyrl' },
  { label: 'Slovak', code: 'slk_Latn' },
  { label: 'Spanish', code: 'spa_Latn' },
  { label: 'Swedish', code: 'swe_Latn' },
  { label: 'Tamil', code: 'tam_Taml' },
  { label: 'Telugu', code: 'tel_Telu' },
  { label: 'Thai', code: 'tha_Thai' },
  { label: 'Turkish', code: 'tur_Latn' },
  { label: 'Ukrainian', code: 'ukr_Cyrl' },
  { label: 'Urdu', code: 'urd_Arab' },
  { label: 'Vietnamese', code: 'vie_Latn' },
];

/**
 * Entity ID of the grammar_check client-side entity. Exported so plugins can
 * target it via the generic {@link useClientEntity} hook. Literal here so the
 * SDK stays bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `GRAMMAR_CHECK_ENTITY_ID` in
 * `packages/entities/src/integrations/grammar-check/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this —
 * drift fails CI.
 */
export const GRAMMAR_CHECK_ENTITY_ID = 'ent_intg_grammar_check_000000000';

/**
 * Generate audio (music today; future speech/SFX sub-types via the same
 * hook) via the bridge's `audio_generate` op. Returns an array of clips
 * with same-origin signed proxy URLs suitable for `<audio src>` playback
 * inside the plugin iframe.
 *
 * Lyria 4×30 s clips can take ~90–120 s end-to-end, so the per-request
 * timeout is 3 minutes (vs image's 2 minutes).
 *
 * @deprecated Phase 2 of the Invocation Surface Pattern shipped typed
 * per-surface hooks: `useLyriaMusic()` (Lyria), `useElevenLabsTts()`,
 * `useElevenLabsSfx()`, `useElevenLabsMusic()`, etc. They invoke through
 * the `surface_invoke` bridge op with per-surface Zod-validated params.
 * The legacy `audio_generate` op continues to work through the
 * deprecation window. Tracking: docs/architecture/invocation-surface-pattern.md
 * §7 Phase 3.
 *
 * @returns Audio generation API (generate, isLoading, result, error).
 */
export function useAudioGeneration(): AudioGenerationApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AudioGenerationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (params: AudioGenerationParams): Promise<AudioGenerationResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await bridge.request<AudioGenerationResult>('audio_generate', params, 180_000);
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { generate, isLoading, result, error };
}

/**
 * Generic Invocation Surface hook.
 *
 * Plugin code should usually call one of the **generated typed hooks**
 * (e.g. `useElevenLabsTts()`) from `./generated/surfaces` rather than this
 * directly — the typed hooks bake in the `surfaceKey` and infer the param
 * and result types from the surface's Zod schemas.
 *
 * This hook is the hand-written substrate they delegate to. Direct use is
 * fine when invoking a surface by dynamic key (rare).
 *
 * @param surfaceKey - Stable kebab-case key registered with the platform.
 * @param timeoutMs  - Per-request timeout. Defaults to 180s to cover the
 *                     longest-running synchronous surfaces (TTS large texts,
 *                     Lyria multi-clip generations).
 */
export function useSurface<TParams, TResult>(surfaceKey: string, timeoutMs = 180_000) {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const invoke = useCallback(
    async (params: TParams): Promise<TResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await bridge.request<TResult>(
          'surface_invoke',
          { surfaceKey, params },
          timeoutMs,
        );
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge, surfaceKey, timeoutMs],
  );

  return { invoke, isLoading, result, error };
}

/**
 * Navigate within the plugin's route space (`navigateTo`), or open another
 * arche's page (`openArche`, requires the `navigation:open_arche` permission).
 *
 * @returns Navigation API (navigateTo, openArche, currentPath).
 */
export function useFiasNavigation(): FiasNavigationApi {
  const bridge = useBridge();
  const [currentPath, setCurrentPath] = useState(bridge.getCurrentPath());

  useEffect(() => {
    // Covers the "subscribed after init" race — if init already arrived
    // before this effect ran, the init-time listener broadcast missed us,
    // and our initial useState value may or may not be stale depending on
    // whether the render captured pre- or post-init state. Syncing on
    // ready() is cheap and deterministic.
    let cancelled = false;
    bridge.waitForInit().then(() => {
      if (!cancelled) setCurrentPath(bridge.getCurrentPath());
    });
    const unsubscribe = bridge.onNavigationUpdate(setCurrentPath);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [bridge]);

  const navigateTo = useCallback(
    (path: string, opts?: { replace?: boolean }) => {
      // `replace` is spread in only when true, so a plain navigation keeps the
      // exact `{ path }` wire shape shipped plugins already emit.
      bridge.request<void>('navigate', { path, ...(opts?.replace ? { replace: true } : {}) });
    },
    [bridge],
  );

  const openArche = useCallback(
    (
      archeId: string,
      opts?: { newTab?: boolean; path?: string; payload?: ArcheHandoffPayloadInput },
    ) => {
      // `path` / `payload` are spread in only when supplied: an older host
      // ignores unknown keys, but sending `path: undefined` would add a key to
      // every call for no reason and churn the wire shape shipped plugins
      // already emit.
      bridge.request<void>('open_arche', {
        archeId,
        newTab: opts?.newTab === true,
        ...(opts?.path ? { path: opts.path } : {}),
        ...(opts?.payload ? { payload: opts.payload } : {}),
      });
    },
    [bridge],
  );

  // Memoized for the same reason as useFiasStorage. `currentPath` is state, so
  // this identity still changes when the path does — which is correct, and is
  // exactly what a consumer depending on it wants.
  return useMemo(
    () => ({ navigateTo, openArche, currentPath }),
    [navigateTo, openArche, currentPath],
  );
}

/**
 * Like useState, but auto-persists to bridge storage.
 * Survives preview rebuilds during development.
 *
 * @param key - Unique storage key for this piece of state
 * @param initialValue - Default value if nothing is stored
 */
export function usePersistentState<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const bridge = useBridge();
  const [value, setValueInternal] = useState<T>(initialValue);
  const initializedRef = useRef(false);
  const valueRef = useRef<T>(initialValue);
  // Set once the plugin calls the setter. A value set BEFORE the mount read
  // resolves is newer than anything in storage, so the read must not
  // overwrite it — it used to, silently reverting e.g. a click that landed
  // while the read was in flight (the debounced write then persisted the
  // user's value while the UI showed the stale one).
  const setByPluginRef = useRef(false);
  const writer = useMemo(() => createDebouncedWriter(bridge), [bridge]);

  // Load from storage on mount; flush any pending write on unmount/key change
  // so rapid updates in a game loop never lose their last value.
  useEffect(() => {
    invokeEntityOp<{
      content: string | null;
      exists: boolean;
    }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'read', { path: `__state/${key}` })
      .then((result) => {
        if (setByPluginRef.current) {
          initializedRef.current = true;
          return;
        }
        if (result.exists && result.content !== null) {
          try {
            const parsed = JSON.parse(result.content) as T;
            valueRef.current = parsed;
            setValueInternal(parsed);
          } catch {
            // Corrupted data — use initial value
          }
        }
        initializedRef.current = true;
      })
      .catch(() => {
        // A failed/denied storage_read (e.g. the plugin didn't declare
        // `storage:sandbox`) must not become a silent unhandled rejection.
        // Persistence is best-effort — fall back to the initial value and
        // let subsequent writes fail on their own rather than hanging here.
        initializedRef.current = true;
      });
    return () => {
      writer.flush();
    };
  }, [bridge, key, writer]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(valueRef.current) : next;
      setByPluginRef.current = true;
      valueRef.current = resolved;
      setValueInternal(resolved);
      writer.schedule(`__state/${key}`, JSON.stringify(resolved));
    },
    [key, writer],
  );

  return [value, setValue];
}

/** What a plugin sees before init, and on a host without the chrome lane. */
const INERT_CHROME: FiasChromeState = {
  focusMode: false,
  focusModeAvailable: false,
  sidebarAvailable: false,
  sidebarCollapsed: false,
  hostExpandedBar: true,
  hostExpandButton: true,
  opener: null,
};

/**
 * The host chrome around your arche — Fias's standard expand/contract (the
 * internal arches' "focus mode"), the suggested-arches sidebar, and the four
 * controls the platform's focus-mode bar carries (Fias home, Worktop, Fias
 * AI, Collapse), so you can place them where your design wants them.
 *
 * Three levels, your choice:
 *  1. Don't call this. The host draws a small expand button over your app;
 *     expanding shows the host's thin bar with the standard four controls.
 *  2. Call it and draw your own expand button (`setFocusMode`) and sidebar
 *     toggle (`setSidebarCollapsed`); keep the host's bar for the way back.
 *  3. Call it, `setHostExpandedBar(false)`, and draw your own bar from
 *     `goToFiasHome` / `goToWorktop` / `openAssistant` / `setFocusMode(false)`.
 *
 * MOUNTING THIS HOOK TAKES THE CHROME OVER: the host's default expand button
 * goes away, because a plugin that reads chrome state is expected to draw its
 * own affordance. Draw one (or set `showExpandButton: false` in the manifest
 * if you want neither). The host decides what is available — `focusModeAvailable`
 * is false outside the normal signed-in layout and `sidebarAvailable` is false
 * where the manifest declined the sidebar — so gate your controls on them.
 *
 * `supported` is false before init and on an older host; the state is then
 * inert and the setters are no-ops, so you can render unconditionally.
 */
export function useFiasChrome(): FiasChromeApi {
  const bridge = useBridge();
  const [chrome, setChrome] = useState<FiasChromeState | null>(bridge.getChrome());
  const [supported, setSupported] = useState<boolean>(bridge.hasChromeSupport());

  useEffect(() => {
    // Same "subscribed after init" race as useFiasNavigation: sync on ready
    // and subscribe for every later change.
    let cancelled = false;
    bridge.waitForInit().then(() => {
      if (cancelled) return;
      setChrome(bridge.getChrome());
      setSupported(bridge.hasChromeSupport());
    });
    const unsubscribe = bridge.onChromeUpdate((next) => {
      setChrome(next);
      setSupported(bridge.hasChromeSupport());
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [bridge]);

  // The takeover, once per bridge: the host drops its default expand button
  // the moment a plugin reads the chrome. Sent on mount — an older host
  // ignores an unknown message type.
  useEffect(() => {
    bridge.setChrome({ hostExpandButton: false });
  }, [bridge]);

  const setFocusMode = useCallback((on: boolean) => bridge.setChrome({ focusMode: on }), [bridge]);
  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => bridge.setChrome({ sidebarCollapsed: collapsed }),
    [bridge],
  );
  const setHostExpandedBar = useCallback(
    (shown: boolean) => bridge.setChrome({ hostExpandedBar: shown }),
    [bridge],
  );
  const goToFiasHome = useCallback(() => bridge.navigateHost('home'), [bridge]);
  const goToWorktop = useCallback(() => bridge.navigateHost('worktop'), [bridge]);
  const openAssistant = useCallback(() => bridge.openAssistant(), [bridge]);
  return useMemo(
    () => ({
      ...(chrome ?? INERT_CHROME),
      opener: chrome?.opener ?? null,
      supported: supported && chrome !== null,
      setFocusMode,
      setSidebarCollapsed,
      setHostExpandedBar,
      goToFiasHome,
      goToWorktop,
      openAssistant,
    }),
    [
      chrome,
      supported,
      setFocusMode,
      setSidebarCollapsed,
      setHostExpandedBar,
      goToFiasHome,
      goToWorktop,
      openAssistant,
    ],
  );
}

/** The person answers the send sheet; it waits on them, like the picker. */
const SEND_TO_OPENER_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Where the person came from, when a Fias tool opened your arche — the forge
 * page's "Open FiasWorks Studio", the Arche Builder's (ADR-037 D8) — and the
 * two things you can do about it:
 *
 * - `returnTo()` takes the person back there.
 * - `send(file)` offers a Vault file YOUR arche created ("Use in {name}"):
 *   Fias shows the person a sheet naming the file and the destination, and
 *   their Send moves it — into a forge's `public/`, or an Arche Builder
 *   arche's asset library. Requires `opener:send`.
 *
 * `opener` is null when nothing opened your arche; show neither then. Reading
 * it does not take the host chrome over (unlike `useFiasChrome`).
 *
 * @example
 *   const { opener, send, returnTo } = useOpener();
 *   {opener && <button onClick={returnTo}>Return to {opener.name}</button>}
 *   {opener && <button onClick={() => send({ fileId, name: 'pumpkin.png' })}>
 *     Use in {opener.name}</button>}
 */
export function useOpener(): FiasOpenerApi {
  const bridge = useBridge();
  const [opener, setOpener] = useState<FiasOpener | null>(bridge.getChrome()?.opener ?? null);

  useEffect(() => {
    let cancelled = false;
    bridge.waitForInit().then(() => {
      if (!cancelled) setOpener(bridge.getChrome()?.opener ?? null);
    });
    const unsubscribe = bridge.onChromeUpdate((next) => setOpener(next?.opener ?? null));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [bridge]);

  const returnTo = useCallback(() => {
    if (bridge.getChrome()?.opener) bridge.navigateHost('opener');
  }, [bridge]);

  const send = useCallback(
    async (file: { fileId: string; name?: string }): Promise<FiasSendToOpenerResult> => {
      if (!bridge.getChrome()?.opener) {
        throw new FiasBridgeError(
          'Nothing opened this arche, so there is nowhere to send',
          'NO_OPENER',
        );
      }
      let answer: FiasSendToOpenerResult | { error: { code: string; message: string } } | undefined;
      try {
        answer = await bridge.request<
          FiasSendToOpenerResult | { error: { code: string; message: string } }
        >(
          'client_entity_invoke',
          {
            entityId: SEND_TO_OPENER_ENTITY_ID,
            input: { fileId: file.fileId, ...(file.name ? { name: file.name } : {}) },
          },
          SEND_TO_OPENER_TIMEOUT_MS,
        );
      } catch (err) {
        // A host without it (an older Fias, the dev harness).
        if (isClientEntityUnavailable(err)) {
          throw new FiasBridgeError(
            'Sending to another arche is not available here',
            'SEND_UNAVAILABLE',
          );
        }
        throw err;
      }
      if (answer && 'error' in answer) {
        throw new FiasBridgeError(answer.error.message, answer.error.code);
      }
      return answer as FiasSendToOpenerResult;
    },
    [bridge],
  );

  return useMemo(() => ({ opener, returnTo, send }), [opener, returnTo, send]);
}

/**
 * Manage step navigation for multi-node plugins.
 * Listens for step_navigate messages from the host to support
 * external navigation (e.g., clicking graph nodes).
 *
 * Pass `{ persistKey }` to make the current step survive preview rebuilds.
 * This is the correct way to persist step state — do NOT additionally wrap
 * currentStep in `usePersistentState`, which creates two independent state
 * sources and a bidirectional sync loop.
 *
 * @param initialStep - Optional initial step/node ID.
 * @param options - Optional config; set `persistKey` to enable persistence.
 * @returns Step navigation API (currentStep, setCurrentStep).
 */
export function useStepNavigation(
  initialStep?: string,
  options?: StepNavigationOptions,
): StepNavigationApi {
  const bridge = useBridge();
  const persistKey = options?.persistKey;
  const [currentStep, setCurrentStepInternal] = useState<string | null>(initialStep ?? null);
  const initializedRef = useRef(false);
  const writer = useMemo(() => createDebouncedWriter(bridge), [bridge]);

  // Load persisted value on mount when persistKey is provided; flush any
  // pending write on unmount/key change so we don't lose the last nav.
  useEffect(() => {
    if (!persistKey) {
      initializedRef.current = true;
      return;
    }
    let cancelled = false;
    invokeEntityOp<{ content: string | null; exists: boolean }>(
      bridge,
      PLUGIN_STORAGE_ENTITY_ID,
      'read',
      { path: `__state/${persistKey}` },
    )
      .then((result) => {
        // Don't clobber a user-initiated setCurrentStep that landed before this resolved.
        if (cancelled || initializedRef.current) return;
        if (result.exists && result.content !== null) {
          try {
            const parsed = JSON.parse(result.content);
            if (typeof parsed === 'string' || parsed === null) {
              setCurrentStepInternal(parsed);
            }
          } catch {
            // Corrupted data — keep initial value
          }
        }
        initializedRef.current = true;
      })
      .catch(() => {
        initializedRef.current = true;
      });
    return () => {
      cancelled = true;
      writer.flush();
    };
  }, [bridge, persistKey, writer]);

  // Subscribe to host-driven step navigation.
  useEffect(() => {
    return bridge.onStepNavigationUpdate((nodeId) => {
      setCurrentStepInternal(nodeId);
      if (persistKey) {
        writer.schedule(`__state/${persistKey}`, JSON.stringify(nodeId));
      }
    });
  }, [bridge, persistKey, writer]);

  const setCurrentStep = useCallback(
    (nodeId: string | null) => {
      initializedRef.current = true;
      setCurrentStepInternal(nodeId);
      if (persistKey) {
        writer.schedule(`__state/${persistKey}`, JSON.stringify(nodeId));
      }
    },
    [persistKey, writer],
  );

  return { currentStep, setCurrentStep };
}

/**
 * Access the Arche Data Store for structured, queryable data persistence.
 * Requires the `data:store` permission in fias-plugin.json.
 *
 * Collections can be 'user'-scoped (each user sees only their own data)
 * or 'shared' (all users of the arche share data).
 *
 * @example
 * const dataStore = useFiasDataStore();
 *
 * // Create a collection (once)
 * await dataStore.createCollection('scores', { userScope: 'user' });
 *
 * // Store a document
 * await dataStore.put('scores', 'level-1', { score: 100, stars: 3 });
 *
 * // Get a document
 * const doc = await dataStore.get<{ score: number }>('scores', 'level-1');
 *
 * // Query with filters
 * const results = await dataStore.query('scores', {
 *   filters: [{ field: 'score', op: 'gte', value: 50 }],
 *   orderBy: { field: 'score', direction: 'desc' },
 *   limit: 10,
 * });
 */
export function useFiasDataStore(): FiasDataStoreApi {
  const bridge = useBridge();

  const createCollection = useCallback(
    async (
      name: string,
      options?: {
        userScope?: 'user' | 'shared' | 'workspace';
        searchable?: { field: string };
        writeMinRole?: WorkspaceRole;
        readMinRole?: WorkspaceRole;
        writePolicy?: DataStoreWritePolicy;
        constraints?: DataStoreCollectionConstraints;
      },
    ): Promise<DataStoreCollection> => {
      const res = await invokeEntityOp<{ collection: DataStoreCollection }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'create_collection',
        {
          name,
          userScope: options?.userScope ?? 'user',
          searchable: options?.searchable,
          writeMinRole: options?.writeMinRole,
          readMinRole: options?.readMinRole,
          writePolicy: options?.writePolicy,
          constraints: options?.constraints,
        },
      );
      return res.collection;
    },
    [bridge],
  );

  const listCollections = useCallback(async (): Promise<DataStoreCollection[]> => {
    const res = await invokeEntityOp<{ collections: DataStoreCollection[] }>(
      bridge,
      PLUGIN_DATASTORE_ENTITY_ID,
      'list_collections',
    );
    return res.collections;
  }, [bridge]);

  const deleteCollection = useCallback(
    async (name: string): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'delete_collection', { name });
    },
    [bridge],
  );

  const put = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string | null,
      data: T,
      options?: DataStoreScopeOptions,
    ): Promise<DataStorePutResult> => {
      const res = await invokeEntityOp<{
        version?: number;
        key?: string;
        audience?: string;
        attestation?: DataStoreAttestation;
      }>(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'put', {
        collection,
        // No key: the server makes one (collections with audiences, ADR-034).
        ...(key !== null ? { key } : {}),
        data,
        workspaceId: options?.workspaceId,
        ...(options?.audience !== undefined ? { audience: options.audience } : {}),
      });
      return {
        version: res?.version,
        ...(res?.key !== undefined ? { key: res.key } : {}),
        ...(res?.audience !== undefined ? { audience: res.audience } : {}),
        ...(res?.attestation !== undefined ? { attestation: res.attestation } : {}),
      };
    },
    [bridge],
  );

  const create = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      data: T,
      options: DataStoreScopeOptions & { audience: string },
    ) => {
      const result = await put(collection, null, data, options);
      if (result.key === undefined) {
        // A host that predates server-generated keys cannot have made one.
        throw new FiasBridgeError(
          'This Fias does not support creating documents in audiences yet',
          'AUDIENCES_UNAVAILABLE',
        );
      }
      return { ...result, key: result.key };
    },
    [put],
  );

  const get = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string,
      options?: DataStoreScopeOptions,
    ): Promise<T | null> => {
      const res = await invokeEntityOp<{ data: T | null }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'get',
        {
          collection,
          key,
          workspaceId: options?.workspaceId,
          ...(options?.audience !== undefined ? { audience: options.audience } : {}),
        },
      );
      return res.data;
    },
    [bridge],
  );

  const getDocument = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string,
      options?: DataStoreScopeOptions,
    ): Promise<DataStoreDocument<T> | null> => {
      const res = await invokeEntityOp<{
        data: T | null;
        key?: string;
        updatedAt?: string;
        version?: number;
        authoredByCollaborator?: boolean;
        audience?: string;
        attestation?: DataStoreAttestation;
      }>(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'get', {
        collection,
        key,
        workspaceId: options?.workspaceId,
        ...(options?.audience !== undefined ? { audience: options.audience } : {}),
      });
      if (res.data === null || res.data === undefined) return null;
      return {
        key: res.key ?? key,
        data: res.data,
        updatedAt: res.updatedAt ?? '',
        ...(res.version !== undefined ? { version: res.version } : {}),
        ...(res.authoredByCollaborator !== undefined
          ? { authoredByCollaborator: res.authoredByCollaborator }
          : {}),
        ...(res.audience !== undefined ? { audience: res.audience } : {}),
        ...(res.attestation !== undefined ? { attestation: res.attestation } : {}),
      };
    },
    [bridge],
  );

  const query = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      options?: DataStoreQueryOptions,
      scope?: DataStoreScopeOptions,
    ): Promise<DataStoreQueryResult<T>> => {
      return invokeEntityOp<DataStoreQueryResult<T>>(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'query', {
        collection,
        ...options,
        // `query`/`search` are the only doc methods that split options from
        // scope, so `{ workspaceId }` lands in the options slot by mistake —
        // and overwriting unconditionally turned that into a SILENT scope
        // loss: the payload went out with no workspaceId and the collection
        // answered WORKSPACE_REQUIRED, pointing the author at the collection
        // rather than at the call. Take the scope arg when it carries one and
        // otherwise leave whatever the spread put there.
        ...(scope?.workspaceId !== undefined ? { workspaceId: scope.workspaceId } : {}),
        ...(scope?.audience !== undefined ? { audience: scope.audience } : {}),
      });
    },
    [bridge],
  );

  const search = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      queryText: string,
      options?: DataStoreSearchOptions,
      scope?: DataStoreScopeOptions,
    ): Promise<DataStoreSearchMatch<T>[]> => {
      const res = await invokeEntityOp<{ matches: DataStoreSearchMatch<T>[] }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'search',
        {
          collection,
          query: queryText,
          ...options,
          // See `query` above — never blank a caller-supplied workspaceId.
          ...(scope?.workspaceId !== undefined ? { workspaceId: scope.workspaceId } : {}),
          ...(scope?.audience !== undefined ? { audience: scope.audience } : {}),
        },
      );
      return res.matches;
    },
    [bridge],
  );

  const deleteDoc = useCallback(
    async (collection: string, key: string, options?: DataStoreScopeOptions): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'delete', {
        collection,
        key,
        workspaceId: options?.workspaceId,
      });
    },
    [bridge],
  );

  const batch = useCallback(
    async (
      operations: DataStoreBatchOp[],
      options?: DataStoreBatchOptions,
    ): Promise<DataStoreBatchResult> => {
      const res = await invokeEntityOp<DataStoreBatchResult>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'batch',
        {
          operations,
          ...(options?.preconditions?.length ? { preconditions: options.preconditions } : {}),
        },
      );
      return { written: res?.written };
    },
    [bridge],
  );

  return useMemo(
    () => ({
      createCollection,
      listCollections,
      deleteCollection,
      put,
      create,
      get,
      getDocument,
      query,
      search,
      delete: deleteDoc,
      batch,
    }),
    [
      createCollection,
      listCollections,
      deleteCollection,
      put,
      create,
      get,
      getDocument,
      query,
      search,
      deleteDoc,
      batch,
    ],
  );
}

/** A member target on the wire: a typed username, or the app's ref (ADR-040 §4). */
function memberTargetPayload(
  member: WorkspaceMemberTarget,
): { username: string } | { ref: string } {
  if (typeof member === 'string') return { username: member };
  if (member && typeof member === 'object' && typeof member.ref === 'string') {
    return { ref: member.ref };
  }
  // Most often `m.username` from a roster: apps created after app user refs
  // get `ref` there instead (ADR-040 §4).
  throw new Error(
    'useFiasWorkspaces: name the member by { ref } from listMembers() (or a username a person typed)',
  );
}

/**
 * Access workspace tenancy — team/org workspaces with role-gated membership
 * (owner/admin/member/viewer) over `workspace`-scoped Data Store collections
 * (ADR-010). The creating user becomes the founding owner; a workspace always
 * keeps at least one owner.
 *
 * Requires the `data:workspace` permission in fias-plugin.json. To read/write a
 * workspace's documents, use `useFiasDataStore()` with `{ workspaceId }` on a
 * collection created with `userScope: 'workspace'`.
 *
 * @example
 * const workspaces = useFiasWorkspaces();
 * const ws = await workspaces.create('Acme Corp');
 * // a person picks who to add, in a sheet Fias draws (ADR-040):
 * const { added } = await workspaces.addPeople(ws.workspaceId);
 * // or, inside a Fias org workspace, its members' shared space:
 * const shared = await workspaces.openForWorkspace();
 * // then, with useFiasDataStore():
 * await dataStore.put('ledger', 'tx-1', { amount: 100 }, { workspaceId: ws.workspaceId });
 *
 * @returns Workspace API with lifecycle + membership methods.
 */
export function useFiasWorkspaces(): FiasWorkspacesApi {
  const bridge = useBridge();

  const create = useCallback(
    async (displayName: string): Promise<DataStoreWorkspace> => {
      const res = await invokeEntityOp<{ workspace: DataStoreWorkspace }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'create',
        { displayName },
      );
      return res.workspace;
    },
    [bridge],
  );

  const openForWorkspace = useCallback(async (): Promise<DataStoreWorkspace> => {
    // The workspace is the host's acting one, stamped on the call by the host;
    // the plugin never names it.
    const res = await invokeEntityOp<{ workspace: DataStoreWorkspace }>(
      bridge,
      PLUGIN_WORKSPACES_ENTITY_ID,
      'create',
      { forActingHolder: true },
    );
    return res.workspace;
  }, [bridge]);

  const list = useCallback(async (): Promise<DataStoreWorkspace[]> => {
    const res = await invokeEntityOp<{ workspaces: DataStoreWorkspace[] }>(
      bridge,
      PLUGIN_WORKSPACES_ENTITY_ID,
      'list',
    );
    return res.workspaces;
  }, [bridge]);

  const get = useCallback(
    async (workspaceId: string): Promise<DataStoreWorkspace> => {
      const res = await invokeEntityOp<{ workspace: DataStoreWorkspace }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'get',
        { workspaceId },
      );
      return res.workspace;
    },
    [bridge],
  );

  const archive = useCallback(
    async (workspaceId: string): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_WORKSPACES_ENTITY_ID, 'archive', { workspaceId });
    },
    [bridge],
  );

  const resolvePeople = useCallback(
    async (refs: string[], workspaceId?: string): Promise<(FiasPerson | null)[]> => {
      // Any length, in order: nothing to ask for an empty list, and the
      // platform's 100-per-call limit is the SDK's to page, not the app's.
      const people: (FiasPerson | null)[] = [];
      for (let i = 0; i < refs.length; i += RESOLVE_PEOPLE_PAGE) {
        const page = refs.slice(i, i + RESOLVE_PEOPLE_PAGE);
        const res = await invokeEntityOp<{ people: (FiasPerson | null)[] }>(
          bridge,
          PLUGIN_WORKSPACES_ENTITY_ID,
          'resolve_people',
          workspaceId === undefined ? { refs: page } : { refs: page, workspaceId },
        );
        people.push(...res.people);
      }
      return people;
    },
    [bridge],
  );

  const listMembers = useCallback(
    async (workspaceId: string): Promise<DataStoreWorkspaceMember[]> => {
      const res = await invokeEntityOp<{ members: DataStoreWorkspaceMember[] }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'list_members',
        { workspaceId },
      );
      return res.members;
    },
    [bridge],
  );

  const addPeople = useCallback(
    async (
      workspaceId: string,
      options?: { role?: 'admin' | 'member' | 'viewer' },
    ): Promise<{ added: AddedWorkspacePerson[] }> => {
      type Answer =
        | { added: AddedWorkspacePerson[] }
        | { error: { code: string; message: string } };
      let answer: Answer;
      try {
        answer = await bridge.request<Answer>(
          'client_entity_invoke',
          {
            entityId: WORKSPACE_ADD_PEOPLE_ENTITY_ID,
            input: options?.role ? { workspaceId, role: options.role } : { workspaceId },
          },
          ADD_PEOPLE_SHEET_TIMEOUT_MS,
        );
      } catch (err) {
        // A host without the sheet (an older Fias or dev harness) has no
        // executor for it: the sheet is simply not available there.
        if (isClientEntityUnavailable(err)) {
          throw new FiasBridgeError(
            'Adding people is not available here',
            'ADD_PEOPLE_UNAVAILABLE',
          );
        }
        throw err;
      }
      if ('error' in answer) throw new FiasBridgeError(answer.error.message, answer.error.code);
      return { added: answer.added };
    },
    [bridge],
  );

  const addMember = useCallback(
    async (
      workspaceId: string,
      username: string,
      role: WorkspaceRole,
      expiresAt?: string,
    ): Promise<DataStoreWorkspaceMember> => {
      const res = await invokeEntityOp<{ member: DataStoreWorkspaceMember }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'add_member',
        {
          workspaceId,
          username,
          role,
          expiresAt,
        },
      );
      return res.member;
    },
    [bridge],
  );

  const updateMember = useCallback(
    async (
      workspaceId: string,
      member: WorkspaceMemberTarget,
      role: WorkspaceRole,
    ): Promise<DataStoreWorkspaceMember> => {
      const res = await invokeEntityOp<{ member: DataStoreWorkspaceMember }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'update_member',
        { workspaceId, ...memberTargetPayload(member), role },
      );
      return res.member;
    },
    [bridge],
  );

  const removeMember = useCallback(
    async (workspaceId: string, member: WorkspaceMemberTarget): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_WORKSPACES_ENTITY_ID, 'remove_member', {
        workspaceId,
        ...memberTargetPayload(member),
      });
    },
    [bridge],
  );

  return useMemo(
    () => ({
      create,
      openForWorkspace,
      list,
      get,
      archive,
      listMembers,
      addPeople,
      addMember,
      updateMember,
      removeMember,
      resolvePeople,
    }),
    [
      create,
      openForWorkspace,
      list,
      get,
      archive,
      listMembers,
      addPeople,
      addMember,
      updateMember,
      removeMember,
      resolvePeople,
    ],
  );
}

/** The refusals that mean "no access here" — the workspaces `get` verb's two answers. */
const NO_WORKSPACE_ACCESS_CODES: ReadonlySet<string> = new Set([
  'NOT_A_MEMBER',
  'WORKSPACE_NOT_FOUND',
]);

/**
 * The current user's OWN access in a workspace (ADR-034): their tier
 * (owner/admin/member/viewer), when their membership expires, and — for an
 * app that declares roles, in an org workspace where they are on — the roles
 * that count now and the permissions they grant.
 *
 * **A UI hint, never security.** Use it to show or hide controls — an "Invite"
 * button for admins, an editor for those who may edit. It protects nothing:
 * the platform checks every read and write on arrival, whatever your code
 * shows. Never build a role check of your own on top of it, and never treat a
 * role picker in your app as access control.
 *
 * `access.mode` says which model answered: `'tier'` (no app roles here),
 * `'legacy'` (your app declares roles, but this workspace has not turned them
 * on yet — tier rules apply) or `'roles'`. `can(permission)` is true only in
 * `'roles'` mode. `access.tier` is `null` when the user has no tier there (not
 * a member, an expired membership, or a workspace not reachable from where the
 * app is open). Pass `null`/`undefined` for "no workspace yet" — nothing is
 * fetched.
 *
 * Requires the `data:workspace` permission. Answers only for the signed-in
 * user; it never returns user ids or anything about other members.
 *
 * The answer is a snapshot taken when the workspace is first passed: the hook
 * does not learn of a change made elsewhere while the app stays open (an
 * admin changing the user's tier or roles, a membership expiring). Call
 * `refetch()` when the app has reason to think it changed — after your own
 * invite or role change, or when the platform refuses an action with
 * `FORBIDDEN` or `NOT_A_MEMBER` — so a stale answer does not linger in the UI.
 *
 * Every `useFiasAccess` for one workspace shares one answer and one request,
 * so calling it in each row of a long list costs one bridge call, and a
 * `refetch()`, a host `access_update` or a window focus asks once for all of
 * them.
 *
 * @example
 * const { access, can } = useFiasAccess(workspaceId);
 * const canApprove =
 *   access?.mode === 'roles' ? can('ledger.approve') : access?.tier === 'admin';
 * return canApprove ? <ApproveButton /> : null; // the server still decides
 */
export function useFiasAccess(workspaceId: string | null | undefined): FiasAccessState {
  const bridge = useBridge();
  // What is shown is read from the workspace's SHARED answer during render
  // (keyed by workspace, so a previous workspace's answer can never show,
  // not even on the render where `workspaceId` changes). These copies make
  // React re-render when it changes, and stand in before this instance has
  // subscribed.
  const known = workspaceId ? peekSharedAccess(bridge, workspaceId) : undefined;
  const [settled, setSettled] = useState<SettledAccess | null>(known?.settled ?? null);
  const [fetching, setFetching] = useState<boolean>(known ? known.fetching : !!workspaceId);

  // One shared ask per workspace (see SharedAccess): mounting joins the
  // answer, or the ask in flight, that every other instance shares.
  useEffect(() => {
    if (!workspaceId) return;
    const shared = sharedAccessFor(bridge, workspaceId);
    const sync = () => {
      setSettled(shared.settled);
      setFetching(shared.fetching);
    };
    const unsubscribe = subscribeSharedAccess(shared, sync);
    askSharedAccess(shared, 'ensure');
    sync();
    return unsubscribe;
  }, [bridge, workspaceId]);

  const refetch = useCallback(() => {
    if (workspaceId) askSharedAccess(sharedAccessFor(bridge, workspaceId), 'again');
  }, [bridge, workspaceId]);

  const openAccessDialog = useCallback(async () => {
    let answer: { changed: boolean } | { error: { code: string; message: string } } | undefined;
    try {
      answer = await bridge.request<
        { changed: boolean } | { error: { code: string; message: string } }
      >(
        'client_entity_invoke',
        { entityId: APP_ACCESS_DIALOG_ENTITY_ID, input: {} },
        ACCESS_SHEET_TIMEOUT_MS,
      );
    } catch (err) {
      // A host without the sheet (an older Fias, or an older dev harness) has
      // no executor for it: the sheet is simply not available. Any other
      // bridge error (a missing permission, a call loop) is the app's to fix,
      // so it passes through unchanged.
      if (isClientEntityUnavailable(err)) {
        throw new FiasBridgeError('Managing access is not available here', 'ACCESS_UNAVAILABLE');
      }
      throw err;
    }
    if (answer && 'error' in answer) {
      throw new FiasBridgeError(answer.error.message, answer.error.code);
    }
    const changed = answer?.changed === true;
    if (changed) refetch();
    return { changed };
  }, [bridge, refetch]);

  // A refetch of the same workspace keeps its answer while it loads; any
  // other workspace has no answer until its own arrives.
  const own = workspaceId && settled?.workspaceId === workspaceId ? settled : null;
  const current = known ? known.settled : own;
  const access = current?.access ?? null;
  const error = current?.error ?? null;
  const isLoading = !!workspaceId && (current === null || (known ? known.fetching : fetching));

  const can = useCallback(
    (permission: string) => access?.mode === 'roles' && access.permissions.includes(permission),
    [access],
  );
  // Audiences (ADR-034 phase 2) — UI hints like `can()`.
  const canReadAudience = useCallback(
    (audience: string) =>
      access?.mode === 'roles' && (access.audiences?.readable.includes(audience) ?? false),
    [access],
  );
  const canDiscloseTo = useCallback(
    (audience: string) =>
      access?.mode === 'roles' && (access.audiences?.disclosable.includes(audience) ?? false),
    [access],
  );

  return useMemo(
    () => ({
      access,
      isLoading,
      error,
      refetch,
      can,
      canReadAudience,
      canDiscloseTo,
      openAccessDialog,
    }),
    [access, isLoading, error, refetch, can, canReadAudience, canDiscloseTo, openAccessDialog],
  );
}

/**
 * The last `my_access` answer per workspace, across every `useFiasAccess`
 * instance in this plugin — what a changed answer is noticed against.
 */
const lastAccessAnswers = new Map<string, string>();

/** The least time between two focus-triggered `my_access` asks. */
const FOCUS_REFETCH_MIN_MS = 30_000;

/** How long a person may keep the access sheet open before the call gives up. */
const ACCESS_SHEET_TIMEOUT_MS = 30 * 60 * 1000;
/** The Add people sheet waits on a person, like the access sheet. */
const ADD_PEOPLE_SHEET_TIMEOUT_MS = ACCESS_SHEET_TIMEOUT_MS;

/** `useFiasAccess`'s last answer, with the workspace it answers for. */
interface SettledAccess {
  workspaceId: string;
  access: FiasAccess | null;
  error: Error | null;
}

/**
 * One workspace's `my_access`, shared by every `useFiasAccess` mounted for it
 * on one bridge: one ask in flight and one current answer. A list whose rows
 * each call the hook costs ONE bridge call, not one per row — per-row asks
 * trip the bridge's runaway-loop guard (50 calls in 5 s) and its per-minute
 * limit, and the rows past it would lose their controls. The host's
 * `access_update` and window focus are listened to once per workspace, so
 * each asks once for every instance.
 *
 * Kept while any instance shows it (or an ask is still out), then dropped:
 * the next mount asks afresh.
 */
interface SharedAccess {
  readonly bridge: FiasBridge;
  readonly workspaceId: string;
  /** The last answer — or a first ask's error; null before either. */
  settled: SettledAccess | null;
  /** A first ask is out. A background ask (an answer is already held) never sets it. */
  fetching: boolean;
  /** Whether an answer arrived; a failed background ask keeps it. */
  answered: boolean;
  /** When the last answer arrived — focus asks are throttled. */
  answeredAt: number;
  inFlight: Promise<void> | null;
  /** An ask starts at the end of this tick, for every demand made in it. */
  scheduled: boolean;
  /** Asked for while one was out (it may answer from before a change): ask once more after it. */
  again: boolean;
  readonly listeners: Set<() => void>;
  stopListening: (() => void) | null;
}

const sharedAccessByBridge = new WeakMap<FiasBridge, Map<string, SharedAccess>>();

function peekSharedAccess(bridge: FiasBridge, workspaceId: string): SharedAccess | undefined {
  return sharedAccessByBridge.get(bridge)?.get(workspaceId);
}

function sharedAccessFor(bridge: FiasBridge, workspaceId: string): SharedAccess {
  let byWorkspace = sharedAccessByBridge.get(bridge);
  if (!byWorkspace) {
    byWorkspace = new Map();
    sharedAccessByBridge.set(bridge, byWorkspace);
  }
  let shared = byWorkspace.get(workspaceId);
  if (!shared) {
    shared = {
      bridge,
      workspaceId,
      settled: null,
      fetching: false,
      answered: false,
      answeredAt: 0,
      inFlight: null,
      scheduled: false,
      again: false,
      listeners: new Set(),
      stopListening: null,
    };
    byWorkspace.set(workspaceId, shared);
  }
  return shared;
}

/** Drop a workspace's entry once nothing shows it and no ask is out. */
function releaseSharedAccess(shared: SharedAccess): void {
  if (shared.listeners.size > 0 || shared.inFlight || shared.scheduled) return;
  const byWorkspace = sharedAccessByBridge.get(shared.bridge);
  if (byWorkspace?.get(shared.workspaceId) === shared) byWorkspace.delete(shared.workspaceId);
}

function notifySharedAccess(shared: SharedAccess): void {
  for (const listener of Array.from(shared.listeners)) listener();
}

/**
 * Show a workspace's shared answer. The first instance starts listening for
 * the host's `access_update` (always asks again) and window focus (asks at
 * most once per FOCUS_REFETCH_MIN_MS); the last one to leave stops.
 */
function subscribeSharedAccess(shared: SharedAccess, listener: () => void): () => void {
  shared.listeners.add(listener);
  if (!shared.stopListening) {
    // The answer is a snapshot (ADR-034): ask again when the host says the
    // person's access changed, and when they come back to the window — a
    // grant or a removal made elsewhere shows up without the app wiring
    // anything. Focus is frequent (every click back into the frame), so it
    // is throttled; a host access_update always asks.
    const unsubscribe = shared.bridge.onAccessUpdate(() => askSharedAccess(shared, 'again'));
    const onFocus = () => {
      if (Date.now() - shared.answeredAt >= FOCUS_REFETCH_MIN_MS) {
        askSharedAccess(shared, 'join');
      }
    };
    const win = typeof window === 'undefined' ? null : window;
    win?.addEventListener('focus', onFocus);
    shared.stopListening = () => {
      unsubscribe();
      win?.removeEventListener('focus', onFocus);
    };
  }
  return () => {
    shared.listeners.delete(listener);
    if (shared.listeners.size === 0 && shared.stopListening) {
      shared.stopListening();
      shared.stopListening = null;
    }
    releaseSharedAccess(shared);
  };
}

/**
 * Ask `my_access` for a workspace, at most once for every instance:
 *   - `ensure` (a mount): only if there is no answer and no ask yet — a first
 *     ask that failed is tried again;
 *   - `join` (focus): unless an ask is already out;
 *   - `again` (access_update, refetch(), a changed access sheet): an ask that
 *     starts after this demand — the one about to start, or one more after
 *     the one out, which may answer from before the change.
 * Demands made in the same tick share the ask that starts at its end.
 */
function askSharedAccess(shared: SharedAccess, demand: 'ensure' | 'join' | 'again'): void {
  if (shared.inFlight) {
    if (demand === 'again') shared.again = true;
    return;
  }
  if (shared.scheduled) return;
  if (demand === 'ensure' && shared.answered) return;
  shared.scheduled = true;
  void Promise.resolve().then(() => {
    shared.scheduled = false;
    startSharedAccess(shared);
  });
}

/**
 * Record an answer. The caches it guards are module-wide, so the comparison
 * is too: against the last answer for this workspace (a remounted hook still
 * notices a change). No access at all clears them whatever was seen before.
 */
function settleSharedAccess(shared: SharedAccess, access: FiasAccess): void {
  const answer = JSON.stringify(access);
  const before = lastAccessAnswers.get(shared.workspaceId);
  const noAccess = access.mode === 'tier' && access.tier === null;
  if (noAccess || (before !== undefined && before !== answer)) clearSdkReadCaches();
  lastAccessAnswers.set(shared.workspaceId, answer);
  shared.answered = true;
  shared.answeredAt = Date.now();
  shared.settled = { workspaceId: shared.workspaceId, access, error: null };
}

/**
 * Ask now. A BACKGROUND ask (an answer is already held) does not show as
 * loading, and a failure of one keeps the answer rather than blanking the
 * app's controls; only a first answer reports its error. An answer that comes
 * back when no instance shows the workspace any more is dropped, as a
 * superseded one always was.
 */
function startSharedAccess(shared: SharedAccess): void {
  if (shared.listeners.size === 0) {
    releaseSharedAccess(shared);
    return;
  }
  const { bridge, workspaceId } = shared;
  const background = shared.answered;
  if (!background) {
    shared.fetching = true;
    notifySharedAccess(shared);
  }
  shared.inFlight = (async () => {
    try {
      const res = await invokeEntityOp<{ access: FiasAccess }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'my_access',
        { workspaceId },
      );
      if (shared.listeners.size > 0) settleSharedAccess(shared, res.access);
    } catch (err) {
      if (shared.listeners.size === 0) {
        // Nobody shows this workspace now; the answer has no one to reach.
      } else if (err instanceof FiasBridgeError && NO_WORKSPACE_ACCESS_CODES.has(err.code)) {
        // No access here at all — the biggest narrowing there is.
        settleSharedAccess(shared, { mode: 'tier', workspaceId, tier: null, expiresAt: null });
      } else if (!background) {
        shared.settled = {
          workspaceId,
          access: null,
          error: err instanceof Error ? err : new Error(String(err)),
        };
      }
    } finally {
      shared.fetching = false;
      shared.inFlight = null;
      if (shared.again) {
        shared.again = false;
        askSharedAccess(shared, 'again');
      }
      notifySharedAccess(shared);
      releaseSharedAccess(shared);
    }
  })();
}

/**
 * Access the in-arche purchase (IAP) store.
 * Provides product listing, purchase initiation, entitlement checking,
 * and purchase history. All purchases are mediated by the platform.
 *
 * Requires the `store:purchase` permission in fias-plugin.json.
 *
 * @returns Store API with products, entitlements, and purchase methods.
 */
export function useFiasStore(): FiasStoreApi {
  const bridge = useBridge();
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [entitlements, setEntitlements] = useState<StoreEntitlement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load products and entitlements on mount
  useEffect(() => {
    let mounted = true;

    Promise.all([
      invokeStoreOp<StoreProduct[]>(bridge, 'get_products'),
      invokeStoreOp<StoreEntitlement[]>(bridge, 'get_entitlements'),
    ])
      .then(([prods, ents]) => {
        if (mounted) {
          setProducts(prods);
          setEntitlements(ents);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [bridge]);

  const purchase = useCallback(
    async (
      productIdentifier: string,
      options?: StorePurchaseOptions,
    ): Promise<StorePurchaseResult> => {
      const result = await invokeStoreOp<StorePurchaseResult>(bridge, 'purchase', {
        productIdentifier,
        ...options,
      });

      // If purchase succeeded and returned an entitlement, update local state
      if (result.success && result.entitlement) {
        setEntitlements((prev) => {
          const existing = prev.findIndex(
            (e) => e.productIdentifier === result.entitlement!.productIdentifier,
          );
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = result.entitlement!;
            return updated;
          }
          return [...prev, result.entitlement!];
        });
      }

      return result;
    },
    [bridge],
  );

  const hasEntitlement = useCallback(
    (productIdentifier: string): boolean => {
      return entitlements.some((e) => e.productIdentifier === productIdentifier && e.isActive);
    },
    [entitlements],
  );

  const getPurchaseHistory = useCallback(async (): Promise<StorePurchase[]> => {
    const res = await invokeStoreOp<{ purchases: StorePurchase[] }>(bridge, 'get_purchase_history');
    return res.purchases;
  }, [bridge]);

  const restorePurchases = useCallback(async (): Promise<StoreEntitlement[]> => {
    const restored = await invokeStoreOp<StoreEntitlement[]>(bridge, 'restore');
    setEntitlements(restored);
    return restored;
  }, [bridge]);

  return useMemo(
    () => ({
      products,
      purchase,
      entitlements,
      hasEntitlement,
      getPurchaseHistory,
      restorePurchases,
      isLoading,
    }),
    [
      products,
      purchase,
      entitlements,
      hasEntitlement,
      getPurchaseHistory,
      restorePurchases,
      isLoading,
    ],
  );
}

/**
 * Refresh a presigned download URL this many milliseconds before it actually
 * expires. Catches the case where a render starts with a still-valid URL but
 * the image load completes after the URL has expired.
 */
const DOWNLOAD_URL_REFRESH_BUFFER_MS = 60_000;

interface DownloadUrlCacheEntry {
  url: string;
  expiresAt: number;
  contentType: string | null;
}

/**
 * Process-lifetime cache of presigned download URLs, keyed by documentId.
 * Shared across all useVaultDocuments() consumers in the plugin so 50
 * thumbnails on a page don't each trigger a separate bridge call. Inflight
 * requests are also deduped so a burst of mounts coalesces to one network
 * roundtrip per documentId.
 *
 * The cache is intentionally module-scoped (not React state): React renders
 * are not the right invalidation boundary — presigned URLs are valid for a
 * fixed wall-clock window regardless of component lifecycle.
 */
const downloadUrlCache = new Map<string, DownloadUrlCacheEntry>();
const downloadUrlInflight = new Map<string, Promise<VaultDocumentDownloadUrl>>();

/**
 * Forget every read the SDK caches that the person's access decided — the
 * presigned Vault download URLs, which stay valid for their window whatever
 * happens to the access that minted them. Called by `useFiasAccess` when the
 * person's access answer changes (ADR-034), so a plugin cannot keep serving a
 * URL minted under access the person no longer has. (Content-pack and
 * community-asset URLs are not decided by the person's access.)
 */
export function clearSdkReadCaches(): void {
  downloadUrlGeneration += 1;
  downloadUrlCache.clear();
  downloadUrlInflight.clear();
}

/**
 * Bumped by every clear: a fetch that started before a clear must not put the
 * URL it brings back into the cache afterwards.
 */
let downloadUrlGeneration = 0;

/**
 * Cache-aware fetcher for a presigned download URL. Used internally by
 * `useVaultDocuments().getDownloadUrl()`. Exposed separately so non-React
 * code paths (e.g. PDF export, image preloaders) can hit the same cache
 * without having to plumb a React context through.
 */
export async function fetchVaultDocumentDownloadUrl(
  documentId: string,
): Promise<VaultDocumentDownloadUrl> {
  const now = Date.now();
  const cached = downloadUrlCache.get(documentId);
  if (cached && now < cached.expiresAt - DOWNLOAD_URL_REFRESH_BUFFER_MS) {
    return { url: cached.url, expiresAt: cached.expiresAt, contentType: cached.contentType };
  }
  const inflight = downloadUrlInflight.get(documentId);
  if (inflight) return inflight;

  const bridge = getBridge();
  const generation = downloadUrlGeneration;
  // The server bridge returns `expiresAt` as an ISO 8601 string; the
  // SDK surface uses epoch milliseconds. Normalize here so callers
  // don't have to think about the wire shape.
  const promise = bridge
    .request<{ url: string; expiresAt: string; contentType: string | null }>(
      'vault_documents_download_url',
      { documentId },
    )
    .then((result) => {
      const normalized: VaultDocumentDownloadUrl = {
        url: result.url,
        expiresAt: new Date(result.expiresAt).getTime(),
        contentType: result.contentType,
      };
      if (generation === downloadUrlGeneration) {
        downloadUrlCache.set(documentId, {
          url: normalized.url,
          expiresAt: normalized.expiresAt,
          contentType: normalized.contentType,
        });
      }
      return normalized;
    })
    .finally(() => {
      // Only our own entry: a newer request may have replaced it.
      if (downloadUrlInflight.get(documentId) === promise) downloadUrlInflight.delete(documentId);
    });
  downloadUrlInflight.set(documentId, promise);
  return promise;
}

/**
 * Access the user's Vault Documents, scoped to documents this arche owns.
 *
 * The most common use is `getDownloadUrl(documentId)` for images created
 * via `useImageGeneration()` — those calls return a `documentId`, and this
 * hook lets the plugin refresh the presigned URL after the original ~1h
 * TTL expires (so backgrounds/thumbnails persist across sessions).
 *
 * Permissions:
 *   - `vault:documents:read` for list / read / getDownloadUrl
 *   - `vault:documents:write` for write / update / delete
 *
 * @returns Vault Documents API (list, read, getDownloadUrl, write, update,
 *          delete).
 */
export function useVaultDocuments(): VaultDocumentsApi {
  const bridge = useBridge();

  const list = useCallback(
    async (options?: VaultDocumentsListOptions): Promise<VaultDocumentsListResult> => {
      return bridge.request<VaultDocumentsListResult>('vault_documents_list', {
        search: options?.search,
        limit: options?.limit,
        cursor: options?.cursor,
      });
    },
    [bridge],
  );

  const read = useCallback(
    async (
      documentId: string,
    ): Promise<{ content: string; contentType: string; revision?: string }> => {
      return bridge.request<{ content: string; contentType: string; revision?: string }>(
        'vault_documents_read',
        { documentId },
      );
    },
    [bridge],
  );

  const getDownloadUrl = useCallback(
    (documentId: string): Promise<VaultDocumentDownloadUrl> =>
      fetchVaultDocumentDownloadUrl(documentId),
    // The hook closes over the React context's bridge, but the resolver
    // uses the singleton — they are the same instance in practice. Keeping
    // `bridge` in the deps for symmetry with other hooks that do go through
    // the context-bound bridge.
    [bridge],
  );

  const readBytes = useCallback(
    async (
      documentId: string,
    ): Promise<{
      document: {
        documentId: string;
        name: string;
        mimeType: string | null;
        sizeBytes: number | null;
        sensitivity: string;
        createdAt: string;
        updatedAt: string;
        revision?: string;
      };
      bytes: Uint8Array;
      contentType: string | null;
    }> => {
      const res = await invokeEntityOp<{
        document: {
          documentId: string;
          name: string;
          mimeType: string | null;
          sizeBytes: number | null;
          sensitivity: string;
          createdAt: string;
          updatedAt: string;
          revision?: string;
        };
        bytesBase64: string;
        contentType: string | null;
      }>(bridge, VAULT_ARCHE_DOCUMENTS_ENTITY_ID, 'get_own_document_bytes', { documentId });
      return {
        document: res.document,
        bytes: base64ToBytes(res.bytesBase64),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  const saveContent = useCallback(
    async (params: VaultDocumentSaveContentParams): Promise<VaultDocumentSaveContentResult> => {
      const routed = await routeSaveContent(params.content);
      let res: VaultDocumentSaveContentResult;
      if (routed.lane === 'body') {
        // Small text rides the entity_invoke body.
        res = await invokeEntityOp<VaultDocumentSaveContentResult>(
          bridge,
          VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
          'save_own_document_content',
          {
            documentId: params.documentId,
            content: routed.text,
            contentType: params.contentType,
            expectedRevision: params.expectedRevision,
          },
        );
      } else {
        // Bytes — or text too large for the bridge body — go through the
        // host, which does the presigned PUT the iframe's CSP forbids (same
        // reason `upload` does; see there). `contentKind: 'text'` keeps the
        // text lane's must-be-a-text-type rule in force at any size.
        //
        // Deliberately NO `name` in this payload: a host that predates
        // in-place saves requires one for an upload, so it refuses this
        // message instead of silently creating a NEW document.
        res = await bridge.request<VaultDocumentSaveContentResult>('vault_documents_upload', {
          saveToDocumentId: params.documentId,
          bytes: bytesToBase64(routed.bytes),
          mimeType: params.contentType,
          contentKind: routed.contentKind,
          expectedRevision: params.expectedRevision,
        });
      }
      // The bytes behind any cached download URL just changed.
      downloadUrlCache.delete(params.documentId);
      return res;
    },
    [bridge],
  );

  const write = useCallback(
    async (params: VaultDocumentWriteParams): Promise<{ documentId: string }> => {
      // `write` has no second lane to fall back to, so say so here rather
      // than let the request die as an opaque 413 at the bridge.
      if (
        params.content.length > SAVE_CONTENT_BODY_LANE_MAX_BYTES ||
        !fitsBridgeBodyAsJsonText(params.content)
      ) {
        throw new FiasBridgeError(
          'write() takes small text only: at most 200,000 characters, and small enough to ' +
            'travel in one bridge request once JSON-escaped (quotes, backslashes and newlines ' +
            'double). Use upload() for larger documents.',
          'INVALID_PARAMS',
        );
      }
      return bridge.request<{ documentId: string }>('vault_documents_write', {
        name: params.name,
        content: params.content,
        contentType: params.contentType,
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
      });
    },
    [bridge],
  );

  const update = useCallback(
    async (documentId: string, params: VaultDocumentUpdateParams): Promise<void> => {
      await bridge.request('vault_documents_update', {
        documentId,
        name: params.name,
        tags: params.tags,
      });
      // Rename rewrites the Content-Disposition the URL would carry, so
      // drop any cached URL for this document.
      downloadUrlCache.delete(documentId);
    },
    [bridge],
  );

  const deleteDocument = useCallback(
    async (documentId: string): Promise<void> => {
      await bridge.request('vault_documents_delete', { documentId });
      downloadUrlCache.delete(documentId);
      downloadUrlInflight.delete(documentId);
    },
    [bridge],
  );

  const search = useCallback(
    async (
      query: string,
      options?: VaultDocumentSearchOptions,
    ): Promise<VaultDocumentSearchResult> => {
      return bridge.request<VaultDocumentSearchResult>('vault_documents_search', {
        query,
        topK: options?.topK,
        minSimilarity: options?.minSimilarity,
        includeContent: options?.includeContent,
      });
    },
    [bridge],
  );

  const attach = useCallback(
    async (
      documentId: string,
      target: { regardingType: VaultDocumentReferenceTargetType; regardingId: string },
    ): Promise<VaultDocumentReferenceResult> => {
      return bridge.request<VaultDocumentReferenceResult>('vault_documents_attach', {
        documentId,
        regardingType: target.regardingType,
        regardingId: target.regardingId,
      });
    },
    [bridge],
  );

  const detach = useCallback(
    async (documentId: string, referenceId: string): Promise<void> => {
      await bridge.request('vault_documents_detach', { documentId, referenceId });
    },
    [bridge],
  );

  const uploadInit = useCallback(
    async (
      params: Omit<VaultDocumentUploadParams, 'userVisible' | 'destination'> & {
        sizeBytes: number;
        checksumSha256: string;
      },
    ): Promise<VaultDocumentUploadInitResult> => {
      // No destination here by construction: a save into the user's
      // Documents needs the host's Save dialog, which it can only show when it
      // orchestrates the whole upload. The host REJECTS a `visibility` here.
      return bridge.request<VaultDocumentUploadInitResult>('vault_documents_upload_init', {
        name: params.name,
        mimeType: params.mimeType,
        sizeBytes: params.sizeBytes,
        checksumSha256: params.checksumSha256,
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
      });
    },
    [bridge],
  );

  const uploadFinalize = useCallback(
    async (params: {
      documentId: string;
      checksumSha256: string;
    }): Promise<VaultDocumentUploadResult> => {
      return bridge.request<VaultDocumentUploadResult>('vault_documents_upload_finalize', {
        documentId: params.documentId,
        checksumSha256: params.checksumSha256,
      });
    },
    [bridge],
  );

  const upload = useCallback(
    async (
      bytes: ArrayBuffer | Uint8Array,
      params: VaultDocumentUploadParams,
    ): Promise<VaultDocumentUploadResult> => {
      // The presigned PUT must run in the HOST, not here: the plugin iframe's
      // CSP is `connect-src 'none'`, which silently blocks an in-iframe
      // `fetch` PUT in published plugins. The host (parent window, no
      // connect-src restriction) orchestrates init → PUT → finalize via the
      // `vault_documents_upload` bridge op; the presigned URL is backend-issued
      // host-side, so the iframe can't redirect the PUT (no SSRF). The
      // lower-level `uploadInit`/`uploadFinalize` remain for advanced callers.
      const src = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      return bridge.request<VaultDocumentUploadResult>('vault_documents_upload', {
        name: params.name,
        mimeType: params.mimeType,
        bytes: bytesToBase64(src),
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
        // A save into the user's Documents. The host turns this into its Save
        // dialog, which the iframe cannot see or script, and performs the
        // write itself from the user's session. Omitted (the common case)
        // keeps the arche-private default.
        destination: params.destination,
        userVisible: params.userVisible,
      });
    },
    [bridge],
  );

  const saveCopy = useCallback(
    async (
      documentId: string,
      options?: VaultDocumentSaveCopyOptions,
    ): Promise<VaultDocumentUploadResult> => {
      // Rides the update op, intercepted by the host: it reads the bytes and
      // shows the Save dialog. Never forwarded — the original is unchanged.
      return bridge.request<VaultDocumentUploadResult>('vault_documents_update', {
        documentId,
        saveCopy: { ...(options?.suggestedName ? { suggestedName: options.suggestedName } : {}) },
      });
    },
    [bridge],
  );

  return useMemo(
    () => ({
      list,
      read,
      readBytes,
      saveContent,
      getDownloadUrl,
      write,
      update,
      delete: deleteDocument,
      search,
      attach,
      detach,
      upload,
      saveCopy,
      uploadInit,
      uploadFinalize,
    }),
    [
      list,
      read,
      readBytes,
      saveContent,
      getDownloadUrl,
      write,
      update,
      deleteDocument,
      search,
      attach,
      detach,
      upload,
      saveCopy,
      uploadInit,
      uploadFinalize,
    ],
  );
}

/** Encoded-size ceiling of the text lane (`save_own_document_content`). Must
 *  match the server's MAX_OWN_DOCUMENT_SAVE_BYTES; content past it is routed
 *  through the host-uploaded lane rather than refused. */
const SAVE_CONTENT_BODY_LANE_MAX_BYTES = 200_000;

/**
 * What the text may occupy ON THE WIRE. The bridge request body is capped at
 * 256 KB (the server's `PLUGIN_BRIDGE_BODY_LIMIT_BYTES`) and the text travels
 * inside it as a JSON string, where every `"`, `\` and newline doubles and
 * every other control character becomes six bytes. So 200 KB of text can be
 * 400 KB — or 1.2 MB — of request, and the answer is an opaque 413 rather
 * than a save. Routing therefore looks at the SERIALIZED size too, with ~16 KB
 * left for the envelope (ids, revision, content type, JSON keys).
 */
const BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES =
  ENTITY_INVOCATION_REQUEST_LIMITS.textBytes - 16 * 1024;

/** True when `text` fits a bridge request body as a JSON string. Encodes at
 *  most once, and not at all for short strings (a UTF-16 unit is at most six
 *  bytes of JSON). */
function fitsBridgeBodyAsJsonText(text: string, utf8Bytes?: number): boolean {
  if (text.length * 6 <= BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES) return true;
  // JSON escapes are ASCII, so they cost the same in UTF-16 units as in
  // bytes: serialized bytes = UTF-8 bytes + the units escaping added.
  const escapeOverhead = JSON.stringify(text).length - text.length;
  const bytes = utf8Bytes ?? new TextEncoder().encode(text).length;
  return bytes + escapeOverhead <= BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES;
}

/** Mirrors the server's `MAX_OWN_DOCUMENT_PRESIGNED_SAVE_BYTES` — the most an
 *  in-place save accepts, pinned to what `readBytes` / `getBytes` can serve
 *  back, so anything saved can be reopened. */
const SAVE_CONTENT_MAX_BYTES = 15 * 1024 * 1024;

/** `saveContent`'s content, routed: small text rides the bridge body as-is;
 *  everything else is bytes for the host to upload. */
type SaveContentRouting =
  | { lane: 'body'; text: string }
  | { lane: 'host'; bytes: Uint8Array; contentKind: 'text' | 'binary' };

/**
 * Pick the lane for `saveContent` from the content itself, encoding text AT
 * MOST ONCE (a UTF-16 code unit is at most 3 UTF-8 bytes, so short strings
 * are routed without encoding at all) and refusing anything past the cap
 * BEFORE the base64 step — which would otherwise build a 20 MB string on the
 * plugin's main thread for a save the server is certain to refuse.
 */
async function routeSaveContent(
  content: string | Uint8Array | ArrayBuffer | Blob,
): Promise<SaveContentRouting> {
  let routed: SaveContentRouting;
  if (typeof content === 'string') {
    // Short enough that neither limit can be reached, whatever it contains:
    // six bytes of JSON per unit is the worst case, three of UTF-8.
    if (content.length * 6 <= SAVE_CONTENT_BODY_LANE_MAX_BYTES) {
      return { lane: 'body', text: content };
    }
    const bytes = new TextEncoder().encode(content);
    routed =
      bytes.length <= SAVE_CONTENT_BODY_LANE_MAX_BYTES &&
      fitsBridgeBodyAsJsonText(content, bytes.length)
        ? { lane: 'body', text: content }
        : { lane: 'host', bytes, contentKind: 'text' };
  } else {
    routed = { lane: 'host', bytes: await toUint8Array(content), contentKind: 'binary' };
  }
  if (routed.lane === 'host' && routed.bytes.length > SAVE_CONTENT_MAX_BYTES) {
    throw new FiasBridgeError(
      `Content is ${routed.bytes.length} bytes; saveContent accepts at most ` +
        `${SAVE_CONTENT_MAX_BYTES} bytes (15 MB).`,
      'OWN_DOCUMENT_SAVE_TOO_LARGE',
    );
  }
  return routed;
}

async function toUint8Array(content: Uint8Array | ArrayBuffer | Blob): Promise<Uint8Array> {
  if (content instanceof Uint8Array) return content;
  if (content instanceof ArrayBuffer) return new Uint8Array(content);
  return new Uint8Array(await content.arrayBuffer());
}

/** Decode base64 transport back to bytes (`get_consented_document_bytes`). */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Base64-encode bytes for transport to the host's `vault_documents_upload`
 *  op (the host does the presigned PUT — see PluginBridge.handleVaultUpload). */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Access the contributor-curated asset library for this arche.
 *
 * In a PUBLISHED arche the library is a frozen snapshot taken at publish
 * time; uploads the contributor makes afterwards go live with the next
 * publish, which re-snapshots from their current library.
 *
 * In the ARCHE BUILDER PREVIEW it reads the contributor's current
 * uploads directly — i.e. exactly what that next publish would
 * snapshot — so an image looks the same before and after shipping.
 * Only `active` assets appear; a soft-deleted one is already gone.
 *
 * Either way the library can legitimately be empty (nothing uploaded
 * yet), so handle that rather than assuming a match.
 *
 * Each asset has an immutable `assetId`; cache them in your datastore /
 * state, and refresh `signedUrl` via `getUrl(assetId)` when it's about
 * to expire.
 *
 * The library holds images and short sound effects (MP3 / WAV). `list` and
 * `index` return images unless you pass `kinds` (e.g. `{ kinds: ['audio'] }`),
 * and with `kinds` the entries are typed `ArcheLibraryAsset` — narrow on
 * `contentType` before using an image's size. Keeping sounds here is
 * optional; music is not accepted. Sound effects that must play on cue need
 * decoded Web Audio buffers: load library sounds with
 * {@link useArcheAssetBytes}`().getMany` once per session, or bundle them in
 * the plugin's JS (base64) — not in `public/`, which only an `<audio>`
 * element can play.
 *
 * Permission: `assets:read`.
 *
 * @example
 *   const { list, getUrl } = useArcheAssets();
 *   const { entries } = await list({ limit: 50 });
 *   // entries[i].signedUrl is a short-lived URL you can use as <img src>
 */
export function useArcheAssets(): ArcheAssetsApi {
  const bridge = useBridge();

  const list = useCallback(
    async (options?: ArcheAssetsListOptions): Promise<ArcheAssetsListResult<ArcheLibraryAsset>> => {
      return bridge.request<ArcheAssetsListResult<ArcheLibraryAsset>>('arche_assets_list', {
        cursor: options?.cursor,
        limit: options?.limit,
        tags: options?.tags,
        kinds: options?.kinds,
      });
    },
    [bridge],
  ) as ArcheAssetsApi['list'];

  const index = useCallback(
    async (options?: ArcheAssetsIndexOptions): Promise<ArcheAssetsIndexResult> => {
      return bridge.request<ArcheAssetsIndexResult>('arche_assets_index', {
        tags: options?.tags,
        groupBy: options?.groupBy,
        kinds: options?.kinds,
      });
    },
    [bridge],
  );

  const getUrl = useCallback(
    async (assetId: string): Promise<ArcheLibraryAsset> => {
      return bridge.request<ArcheLibraryAsset>('arche_assets_get_url', { assetId });
    },
    [bridge],
  ) as ArcheAssetsApi['getUrl'];

  return useMemo(() => ({ list, index, getUrl }), [list, index, getUrl]);
}

/**
 * Read the bytes of assets in this arche's asset library — for Web Audio
 * (`AudioContext.decodeAudioData`), canvas pixel work, or anything else a
 * signed URL cannot do (the plugin sandbox has no network). Read each sound
 * once per session and keep the decoded buffer.
 *
 * To load a set of sounds, pass the listed entries to `getMany`: up to 100
 * are read per call, so a whole sound set takes one or two calls. One call
 * per sound would trip the bridge's runaway-loop guard (more than 50 calls in
 * 5 seconds), which blocks every read for 10 seconds — `getBytes` calls made
 * together are batched for the same reason. The host page fetches each asset,
 * so each read costs one asset load like an `<img>` does. Unavailable in the
 * builder preview.
 *
 * Sounds are ready to trigger only once their buffers are decoded: wait for
 * the load before the first cue, and resume the `AudioContext` on the first
 * user gesture.
 *
 * Permissions: `entities:client_invoke` AND `assets:read`.
 *
 * @example
 *   const { list } = useArcheAssets();
 *   const { getMany } = useArcheAssetBytes();
 *   const { entries } = await list({ kinds: ['audio'], tags: ['sfx:ui'] });
 *   const ctx = new AudioContext();
 *   const loaded = await getMany(entries);
 *   const buffers = new Map<string, AudioBuffer>();
 *   for (const entry of entries) {
 *     const result = loaded[entry.assetId];
 *     if (result instanceof Error) continue; // result.code says why
 *     buffers.set(entry.name ?? entry.assetId, await ctx.decodeAudioData(result.bytes));
 *   }
 */
export function useArcheAssetBytes(): ArcheAssetBytesApi {
  const bridge = useBridge();

  const getBytes = useCallback(
    async (asset: string | ArcheAssetRef): Promise<ArcheAssetBytes> => {
      let ref: ArcheAssetRef;
      if (typeof asset === 'string') {
        try {
          ref = await bridge.request<ArcheLibraryAsset>('arche_assets_get_url', { assetId: asset });
        } catch (err) {
          throw toArcheAssetError(err);
        }
      } else {
        ref = asset;
      }
      return enqueueAssetByteRead(bridge, ref);
    },
    [bridge],
  );

  const getMany = useCallback(
    async (assets: ArcheAssetRef[]) => {
      const read = await readAssetBytesBatch(bridge, assets);
      const out: Record<string, ArcheAssetBytes | ArcheAssetError> = {};
      for (const [assetId, result] of read) out[assetId] = result;
      return out;
    },
    [bridge],
  );

  return useMemo(() => ({ getBytes, getMany }), [getBytes, getMany]);
}

/**
 * Entity ID of the arche asset bytes client-side entity — the host-side
 * reader behind {@link useArcheAssetBytes}. Literal here so the SDK
 * stays bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `ARCHE_ASSET_BYTES_ENTITY_ID` in
 * `packages/entities/src/integrations/arche-asset-bytes/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this.
 */
export const ARCHE_ASSET_BYTES_ENTITY_ID = 'ent_intg_arche_asset_bytes_00000';

/** A failed asset byte read (`useArcheAssetBytes`): `code` says why. */
export class ArcheAssetError extends Error {
  constructor(
    message: string,
    readonly code: ArcheAssetErrorCode,
  ) {
    super(message);
    this.name = 'ArcheAssetError';
  }
}

/** A listed URL with less than this many seconds left is replaced, not reused. */
const ASSET_URL_MIN_REMAINING_SECONDS = 30;

/** Most URLs the host reads in one call (`ARCHE_ASSET_BYTES_MAX_URLS` in the executor). */
const ASSET_BYTES_MAX_URLS_PER_CALL = 100;

/**
 * Most lapsed URLs one batch replaces with `arche_assets_get_url`, one call
 * each. Covers URLs that lapse in flight while staying far below the bridge's
 * 50-calls-in-5-seconds guard; a batch of older entries is answered
 * `ASSET_URL_EXPIRED` past this, so the plugin lists again (one call).
 */
const ASSET_URL_MAX_REFRESHES_PER_BATCH = 10;

type ArcheAssetBytesFileResponse =
  | ArcheAssetBytes
  | { error: { code: ArcheAssetErrorCode; message: string } };

type ArcheAssetBytesResponse =
  | { results: ArcheAssetBytesFileResponse[] }
  | { error: { code: ArcheAssetErrorCode; message: string } };

/** One `read` call: the outcome of each URL, in order. Throws when the call itself is refused. */
async function readSignedUrls(
  bridge: FiasBridge,
  signedUrls: string[],
): Promise<ArcheAssetBytesFileResponse[]> {
  let response: ArcheAssetBytesResponse;
  try {
    response = await bridge.request<ArcheAssetBytesResponse>(
      'client_entity_invoke',
      { entityId: ARCHE_ASSET_BYTES_ENTITY_ID, input: { verb: 'read', signedUrls } },
      CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
    );
  } catch (err) {
    throw toArcheAssetError(err);
  }
  if ('error' in response) {
    throw new ArcheAssetError(response.error.message, response.error.code);
  }
  return response.results;
}

/** A URL to read for one asset; `refreshed` when it came from a fresh `get_url`. */
interface PendingAssetRead {
  assetId: string;
  signedUrl: string;
  refreshed: boolean;
}

function fileError(result: ArcheAssetBytesFileResponse | undefined): ArcheAssetError {
  return result && 'error' in result
    ? new ArcheAssetError(result.error.message, result.error.code)
    : new ArcheAssetError('the asset could not be read', 'ASSET_UNAVAILABLE');
}

/**
 * Read `items` in calls of up to 100 URLs, recording each outcome in `out`.
 * Assets that did not fit a call's byte budget beside the others go again
 * together until a pass reads nothing more (then each is too large on its
 * own). Returns the reused URLs that lapsed in flight, for a fresh URL.
 */
async function readInBatches(
  bridge: FiasBridge,
  items: PendingAssetRead[],
  out: Map<string, ArcheAssetBytes | ArcheAssetError>,
): Promise<PendingAssetRead[]> {
  const lapsed: PendingAssetRead[] = [];
  let queue = items;
  while (queue.length > 0) {
    const overBudget: PendingAssetRead[] = [];
    let readCount = 0;
    for (let at = 0; at < queue.length; at += ASSET_BYTES_MAX_URLS_PER_CALL) {
      const chunk = queue.slice(at, at + ASSET_BYTES_MAX_URLS_PER_CALL);
      const results = await readSignedUrls(
        bridge,
        chunk.map((item) => item.signedUrl),
      );
      chunk.forEach((item, i) => {
        const result = results[i];
        if (result && !('error' in result)) {
          out.set(item.assetId, result);
          readCount += 1;
        } else if (result?.error.code === 'ASSET_URL_EXPIRED' && !item.refreshed) {
          // A reused URL can lapse before the host's fetch lands (or the
          // device clock runs fast); a fresh one recovers it.
          lapsed.push(item);
        } else if (result?.error.code === 'ASSET_TOO_LARGE' && chunk.length > 1) {
          overBudget.push(item);
        } else {
          out.set(item.assetId, fileError(result));
        }
      });
    }
    if (readCount === 0) {
      for (const item of overBudget) {
        out.set(
          item.assetId,
          new ArcheAssetError('the asset is too large to read', 'ASSET_TOO_LARGE'),
        );
      }
      break;
    }
    queue = overBudget;
  }
  return lapsed;
}

/**
 * Read `refs` in as few host calls as possible: reuse each listed URL while it
 * has time left, replace a bounded number of lapsed ones, and send up to 100
 * URLs per call. Throws only when a call is refused as a whole.
 */
async function readAssetBytesBatch(
  bridge: FiasBridge,
  refs: ArcheAssetRef[],
): Promise<Map<string, ArcheAssetBytes | ArcheAssetError>> {
  const out = new Map<string, ArcheAssetBytes | ArcheAssetError>();
  let refreshesLeft = ASSET_URL_MAX_REFRESHES_PER_BATCH;
  // A fresh URL for each lapsed item, within the batch's refresh allowance.
  const refresh = async (assetIds: string[]): Promise<PendingAssetRead[]> => {
    const fresh: PendingAssetRead[] = [];
    for (const assetId of assetIds) {
      if (refreshesLeft <= 0) {
        out.set(
          assetId,
          new ArcheAssetError(
            'the asset URL has expired; list the assets again for fresh URLs',
            'ASSET_URL_EXPIRED',
          ),
        );
        continue;
      }
      refreshesLeft -= 1;
      try {
        const asset = await bridge.request<ArcheLibraryAsset>('arche_assets_get_url', { assetId });
        fresh.push({ assetId, signedUrl: asset.signedUrl, refreshed: true });
      } catch (err) {
        out.set(assetId, toArcheAssetError(err));
      }
    }
    return fresh;
  };

  // One read per asset, whatever the caller passed twice.
  const unique = new Map<string, ArcheAssetRef>();
  for (const ref of refs) if (!unique.has(ref.assetId)) unique.set(ref.assetId, ref);

  const nowSeconds = Date.now() / 1000;
  const reusable: PendingAssetRead[] = [];
  const stale: string[] = [];
  for (const ref of unique.values()) {
    if (ref.expiresAt - nowSeconds > ASSET_URL_MIN_REMAINING_SECONDS) {
      reusable.push({ assetId: ref.assetId, signedUrl: ref.signedUrl, refreshed: false });
    } else {
      stale.push(ref.assetId);
    }
  }
  const pending = [...reusable, ...(await refresh(stale))];
  const lapsed = await readInBatches(bridge, pending, out);
  if (lapsed.length > 0) {
    await readInBatches(bridge, await refresh(lapsed.map((item) => item.assetId)), out);
  }
  return out;
}

/** `getBytes` calls made in the same tick, per bridge — sent as one batch. */
interface PendingAssetByteReads {
  refs: ArcheAssetRef[];
  waiters: Array<{
    assetId: string;
    resolve: (bytes: ArcheAssetBytes) => void;
    reject: (err: ArcheAssetError) => void;
  }>;
}

const pendingAssetByteReads = new WeakMap<FiasBridge, PendingAssetByteReads>();

function enqueueAssetByteRead(bridge: FiasBridge, ref: ArcheAssetRef): Promise<ArcheAssetBytes> {
  let batch = pendingAssetByteReads.get(bridge);
  if (!batch) {
    const created: PendingAssetByteReads = { refs: [], waiters: [] };
    pendingAssetByteReads.set(bridge, created);
    batch = created;
    void Promise.resolve().then(() => flushAssetByteReads(bridge, created));
  }
  batch.refs.push(ref);
  const queued = batch;
  return new Promise<ArcheAssetBytes>((resolve, reject) => {
    queued.waiters.push({ assetId: ref.assetId, resolve, reject });
  });
}

async function flushAssetByteReads(bridge: FiasBridge, batch: PendingAssetByteReads) {
  pendingAssetByteReads.delete(bridge);
  let results: Map<string, ArcheAssetBytes | ArcheAssetError>;
  try {
    results = await readAssetBytesBatch(bridge, batch.refs);
  } catch (err) {
    const failure = toArcheAssetError(err);
    for (const waiter of batch.waiters) waiter.reject(failure);
    return;
  }
  const handedOut = new Set<string>();
  for (const waiter of batch.waiters) {
    const result =
      results.get(waiter.assetId) ??
      new ArcheAssetError('the asset could not be read', 'ASSET_UNAVAILABLE');
    if (result instanceof ArcheAssetError) {
      waiter.reject(result);
    } else if (handedOut.has(waiter.assetId)) {
      // Two callers asked for one asset: each gets its own buffer, because
      // `decodeAudioData` detaches the one it is given.
      waiter.resolve({ ...result, bytes: result.bytes.slice(0) });
    } else {
      handedOut.add(waiter.assetId);
      waiter.resolve(result);
    }
  }
}

function toArcheAssetError(err: unknown): ArcheAssetError {
  if (err instanceof ArcheAssetError) return err;
  const message = err instanceof Error ? err.message : String(err);
  const code = (err as { code?: unknown } | null)?.code;
  if (/permission denied/i.test(message)) {
    return new ArcheAssetError(
      `${message} — useArcheAssetBytes needs both "assets:read" and "entities:client_invoke" in fias-plugin.json`,
      'PERMISSION_DENIED',
    );
  }
  if (code === 'RATE_LIMITED' || code === 'RATE_LIMIT' || /rate limit/i.test(message)) {
    return new ArcheAssetError(message, 'RATE_LIMIT');
  }
  if (/rapid-fire loop|runaway loop/i.test(message)) {
    return new ArcheAssetError(
      `${message} Load many assets with useArcheAssetBytes().getMany(entries) — one call per 100.`,
      'RATE_LIMIT',
    );
  }
  return new ArcheAssetError(message, 'ASSET_UNAVAILABLE');
}

/**
 * Entity ID of the plugin content pack client-side entity (ADR-021) — the
 * host-side reader behind {@link useFiasContent}. Literal here so the SDK
 * stays bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `PLUGIN_CONTENT_ENTITY_ID` in
 * `packages/entities/src/integrations/plugin-content/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this.
 */
export const PLUGIN_CONTENT_ENTITY_ID = 'ent_intg_plugin_content_00000000';

/**
 * Entity ID of the plugin local store client-side entity (ADR-031) — the
 * host-side store behind {@link useLocalStore}. Literal here so the SDK stays
 * bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `PLUGIN_LOCAL_STORE_ENTITY_ID` in
 * `packages/entities/src/integrations/plugin-local-store/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this.
 */
export const PLUGIN_LOCAL_STORE_ENTITY_ID = 'ent_intg_plugin_local_store_0000';

/** A failed local-store call: `code` says why. */
export class FiasLocalStoreError extends Error {
  constructor(
    message: string,
    public readonly code: FiasLocalStoreErrorCode,
  ) {
    super(message);
    this.name = 'FiasLocalStoreError';
  }
}

type LocalStoreResponse = {
  entry?: FiasLocalStoreEntry | null;
  sizeBytes?: number;
  entries?: FiasLocalStoreEntryMeta[];
  deleted?: number;
  usedBytes?: number;
  budgetBytes?: number;
  error?: { code: FiasLocalStoreErrorCode; message: string };
};

function toLocalStoreError(err: unknown): FiasLocalStoreError {
  if (err instanceof FiasLocalStoreError) return err;
  const message = err instanceof Error ? err.message : String(err);
  // The bridge's centralized gates carry a code; the registry's own refusals
  // (per-entity permission, per-entity rate bucket) are plain Errors, so the
  // message is the fallback, never the first look.
  const raw = (err as { code?: unknown })?.code;
  const code: FiasLocalStoreErrorCode =
    raw === 'PERMISSION_DENIED' || /permission denied/i.test(message)
      ? 'PERMISSION_DENIED'
      : raw === 'RATE_LIMIT' || raw === 'RATE_LIMITED' || /rate limit/i.test(message)
        ? 'RATE_LIMIT'
        : 'LOCAL_STORE_UNAVAILABLE';
  return new FiasLocalStoreError(message, code);
}

/**
 * Keep working data on the user's device — a recovery journal, drafts, a
 * cache — so a refresh, a crash or a closed tab loses nothing. Writes take
 * milliseconds and never touch the network: the host page keeps the data in
 * its own browser storage, in a space only this plugin can read, for the
 * signed-in user in the workspace they are working in (Personal and each org
 * workspace keep separate data; switching workspace switches the store).
 * Signing out clears it.
 *
 * This is NOT where a user's work is kept. It is best-effort device storage —
 * a browser may clear site data, and nothing here reaches another device —
 * so write the journal here and save the user's files through the Vault
 * (`useVaultUserDocuments().saveContent`, `upload`, or the Save dialog).
 *
 * Values: a string, bytes (`ArrayBuffer` / `Uint8Array`), or any JSON value;
 * `get` returns the same kind. One value ≤ 64 MB; the whole store for this
 * user ≤ 256 MB. Over budget, `put` throws `LOCAL_STORE_QUOTA_EXCEEDED`
 * rather than evicting — decide what to keep with `estimate()` and `list()`.
 * For a large snapshot (an image, a big document), put BYTES: a JSON value is
 * validated and serialized on the host page, which costs time proportional
 * to its size on every write, while bytes are only copied. Restore a journal
 * with `getMany(keys)` — one call for up to 100 entries, instead of one call
 * per key.
 *
 * Permission: `storage:local`.
 *
 * @example
 *   const store = useLocalStore();
 *   await store.put(`session/${docId}`, snapshotJson);        // on every change
 *   const saved = await store.getJson<Snapshot>(`session/${docId}`); // on load
 *   await store.delete(`session/${docId}`);                    // on close
 */
export function useLocalStore(): FiasLocalStoreApi {
  const bridge = useBridge();

  const call = useCallback(
    async (input: Record<string, unknown>): Promise<LocalStoreResponse> => {
      let response: LocalStoreResponse;
      try {
        response = await bridge.request<LocalStoreResponse>(
          'client_entity_invoke',
          { entityId: PLUGIN_LOCAL_STORE_ENTITY_ID, input },
          CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
        );
      } catch (err) {
        throw toLocalStoreError(err);
      }
      if (response?.error) {
        throw new FiasLocalStoreError(response.error.message, response.error.code);
      }
      return response ?? {};
    },
    [bridge],
  );

  const get = useCallback(
    async (key: string): Promise<FiasLocalStoreEntry | null> =>
      (await call({ verb: 'get', key })).entry ?? null,
    [call],
  );

  const getText = useCallback(
    async (key: string): Promise<string | null> => {
      const entry = await get(key);
      if (!entry) return null;
      if (typeof entry.value !== 'string') {
        throw new FiasLocalStoreError(`"${key}" does not hold text`, 'LOCAL_STORE_WRONG_KIND');
      }
      return entry.value;
    },
    [get],
  );

  const getBytes = useCallback(
    async (key: string): Promise<ArrayBuffer | null> => {
      const entry = await get(key);
      if (!entry) return null;
      if (!(entry.value instanceof ArrayBuffer)) {
        throw new FiasLocalStoreError(`"${key}" does not hold bytes`, 'LOCAL_STORE_WRONG_KIND');
      }
      return entry.value;
    },
    [get],
  );

  const getJson = useCallback(
    async <T = unknown>(key: string): Promise<T | null> => {
      const entry = await get(key);
      if (!entry) return null;
      if (entry.value instanceof ArrayBuffer) {
        throw new FiasLocalStoreError(`"${key}" holds bytes, not JSON`, 'LOCAL_STORE_WRONG_KIND');
      }
      // A top-level string is always stored as text, so `put(key,
      // JSON.stringify(x))` reads back here the same as `put(key, x)`.
      if (typeof entry.value === 'string') {
        try {
          return JSON.parse(entry.value) as T;
        } catch {
          throw new FiasLocalStoreError(
            `"${key}" holds text that is not JSON`,
            'LOCAL_STORE_WRONG_KIND',
          );
        }
      }
      return entry.value as T;
    },
    [get],
  );

  const getMany = useCallback(
    async (keys: string[]): Promise<Array<FiasLocalStoreEntry | null>> =>
      ((await call({ verb: 'get_many', keys })) as { entries?: Array<FiasLocalStoreEntry | null> })
        .entries ?? [],
    [call],
  );

  const put = useCallback(
    async (key: string, value: FiasLocalStoreValue): Promise<{ sizeBytes: number }> => ({
      sizeBytes: (await call({ verb: 'put', key, value })).sizeBytes ?? 0,
    }),
    [call],
  );

  const del = useCallback(
    async (key: string): Promise<void> => {
      await call({ verb: 'delete', key });
    },
    [call],
  );

  const list = useCallback(
    async (prefix?: string): Promise<FiasLocalStoreEntryMeta[]> =>
      (await call({ verb: 'list', ...(prefix !== undefined ? { prefix } : {}) })).entries ?? [],
    [call],
  );

  const clear = useCallback(
    async (): Promise<{ deleted: number }> => ({
      deleted: (await call({ verb: 'clear' })).deleted ?? 0,
    }),
    [call],
  );

  const estimate = useCallback(async (): Promise<FiasLocalStoreEstimate> => {
    const res = await call({ verb: 'estimate' });
    return { usedBytes: res.usedBytes ?? 0, budgetBytes: res.budgetBytes ?? 0 };
  }, [call]);

  return useMemo(
    () => ({ get, getMany, getText, getBytes, getJson, put, delete: del, list, clear, estimate }),
    [get, getMany, getText, getBytes, getJson, put, del, list, clear, estimate],
  );
}

/** A failed content read: `code` says why, `path` names the file when there is one. */
export class FiasContentError extends Error {
  constructor(
    message: string,
    readonly code: FiasContentErrorCode,
    readonly path?: string,
  ) {
    super(message);
    this.name = 'FiasContentError';
  }
}

type ContentFileResult =
  | { text: string; mimeType: string }
  | { bytes: ArrayBuffer; mimeType: string }
  | { url: string; expiresAt: string }
  | { error: { code: FiasContentErrorCode; message: string } };

interface ContentResponse {
  files?: FiasContentFile[];
  results?: Record<string, ContentFileResult>;
  error?: { code: FiasContentErrorCode; message: string };
}

function toContentError(err: unknown): FiasContentError {
  if (err instanceof FiasContentError) return err;
  const message = err instanceof Error ? err.message : String(err);
  const code = (err as { code?: unknown } | null)?.code;
  if (/permission denied/i.test(message)) return new FiasContentError(message, 'PERMISSION_DENIED');
  if (code === 'RATE_LIMITED' || /rate limit/i.test(message)) {
    return new FiasContentError(message, 'RATE_LIMIT');
  }
  return new FiasContentError(message, 'CONTENT_UNAVAILABLE');
}

function fileResult(
  results: Record<string, ContentFileResult> | undefined,
  path: string,
): ContentFileResult {
  const result = results?.[path];
  if (!result) {
    throw new FiasContentError(`No result for content/${path}`, 'CONTENT_UNAVAILABLE', path);
  }
  if ('error' in result) throw new FiasContentError(result.error.message, result.error.code, path);
  return result;
}

/**
 * `blob:` URLs built in this iframe, per path. A published pack is immutable,
 * so one URL per path serves the plugin for its whole life.
 */
const contentObjectUrls = new Map<string, Promise<string>>();

/**
 * Read this plugin's own content pack — the reviewed `content/` directory it
 * was published with (lessons, data, images, audio, fonts).
 *
 * The host page reads the pack from the Fias content CDN, verifies every file
 * against its hash, and caches it; nothing here touches the network, and a
 * plugin can only read its own pack. Reads are free for your users; the
 * contributor pays the pack's storage and bandwidth, and cached repeat reads
 * cost nothing.
 *
 * Paths are relative to `content/`. In the Arche Builder and developer
 * previews there is no published pack: calls fail with
 * `CONTENT_UNAVAILABLE_IN_PREVIEW` (the dev harness serves your local
 * `content/` instead).
 *
 * Permission: `entities:client_invoke`.
 *
 * @example
 *   const content = useFiasContent();
 *   const lessons = await content.list('lessons/');
 *   const markdown = await content.getText(lessons[0].path);
 *   const cover = await content.getObjectUrl('img/cover.webp'); // <img src={cover}>
 */
export function useFiasContent(): FiasContentApi {
  const bridge = useBridge();

  const call = useCallback(
    async (input: Record<string, unknown>): Promise<ContentResponse> => {
      let response: ContentResponse;
      try {
        response = await bridge.request<ContentResponse>(
          'client_entity_invoke',
          { entityId: PLUGIN_CONTENT_ENTITY_ID, input },
          CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
        );
      } catch (err) {
        throw toContentError(err);
      }
      if (response?.error) {
        throw new FiasContentError(response.error.message, response.error.code);
      }
      return response ?? {};
    },
    [bridge],
  );

  const list = useCallback(
    async (prefix?: string): Promise<FiasContentFile[]> =>
      (await call({ verb: 'list', ...(prefix !== undefined ? { prefix } : {}) })).files ?? [],
    [call],
  );

  const getText = useCallback(
    async (path: string): Promise<string> => {
      const { results } = await call({ verb: 'read', paths: [path], as: 'text' });
      return (fileResult(results, path) as { text: string }).text;
    },
    [call],
  );

  const getJson = useCallback(
    async <T = unknown>(path: string): Promise<T> => JSON.parse(await getText(path)) as T,
    [getText],
  );

  const readBytes = useCallback(
    async (path: string): Promise<{ bytes: ArrayBuffer; mimeType: string }> => {
      const { results } = await call({ verb: 'read', paths: [path], as: 'bytes' });
      return fileResult(results, path) as { bytes: ArrayBuffer; mimeType: string };
    },
    [call],
  );

  const getBytes = useCallback(
    async (path: string): Promise<ArrayBuffer> => (await readBytes(path)).bytes,
    [readBytes],
  );

  const getMany = useCallback(
    async (paths: string[], as: 'text' | 'bytes') => {
      const { results } = await call({ verb: 'read', paths, as });
      const out: Record<string, string | ArrayBuffer | Error> = {};
      for (const path of paths) {
        try {
          const result = fileResult(results, path);
          out[path] =
            as === 'text'
              ? (result as { text: string }).text
              : (result as { bytes: ArrayBuffer }).bytes;
        } catch (err) {
          out[path] = toContentError(err);
        }
      }
      return out;
    },
    [call],
  ) as FiasContentApi['getMany'];

  const getObjectUrl = useCallback(
    async (path: string): Promise<string> => {
      const cached = contentObjectUrls.get(path);
      if (cached) return cached;
      const pending = readBytes(path).then(({ bytes, mimeType }) =>
        URL.createObjectURL(new Blob([bytes], { type: mimeType })),
      );
      contentObjectUrls.set(path, pending);
      pending.catch(() => contentObjectUrls.delete(path));
      return pending;
    },
    [readBytes],
  );

  const getUrl = useCallback(
    async (path: string): Promise<FiasContentUrl> => {
      const { results } = await call({ verb: 'url', paths: [path] });
      const { url, expiresAt } = fileResult(results, path) as FiasContentUrl;
      return { url, expiresAt };
    },
    [call],
  );

  return useMemo(
    () => ({ list, getText, getJson, getBytes, getMany, getObjectUrl, getUrl }),
    [list, getText, getJson, getBytes, getMany, getObjectUrl, getUrl],
  );
}

/**
 * Preserve transient UI state across a builder-preview rebuild.
 *
 * In the arche builder, editing code (or a background cross-device sync)
 * rebuilds the preview, which reloads the iframe and wipes the plugin's
 * in-memory state — losing, for example, a report the user just generated.
 * This hook lets you opt that state into host-side preservation: just before
 * the reload the host snapshots `getState()`, and after the reload it hands the
 * value back to `onRestore`.
 *
 * Inert outside the builder preview (the host never requests a capture), so
 * it's safe to leave in production plugin code.
 *
 * @param key       Stable namespace for this slice of state (so multiple calls
 *                  don't collide). Use a literal like `'report'`.
 * @param getState  Returns the current serializable state. Must be JSON-safe.
 * @param onRestore Receives the snapshot after a reload. Reads the latest
 *                  closure via a ref, so you don't need a stable identity.
 *
 * @example
 * const [report, setReport] = useState<Report | null>(null);
 * useFiasPreviewState('report', () => report, (saved) => setReport(saved as Report));
 */
export function useFiasPreviewState<T>(
  key: string,
  getState: () => T,
  onRestore: (state: T) => void,
): void {
  const bridge = useBridge();
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  useEffect(() => {
    return bridge.registerPreviewState(
      key,
      () => getStateRef.current(),
      (state) => onRestoreRef.current(state as T),
    );
  }, [bridge, key]);
}

/** JSON with object keys sorted at every level, so equal content gives equal text. */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v as Record<string, unknown>)
            .sort()
            .map((k) => [k, (v as Record<string, unknown>)[k]]),
        )
      : v,
  );
}

/**
 * Subscribes to realtime invalidation events through any catalog verb of
 * `kind: 'subscribe'` (ADR-012 Phase M4) — the primitive behind
 * `useDataSubscription`. Notify-then-refetch: the platform pushes a minimal
 * "this changed" event and `onInvalidate` decides what to refetch.
 *
 * `payload` is the verb's wire payload (`{ op, ...params }`), sent to
 * `entity_invoke` for `entityId` exactly as given; the hook re-subscribes
 * when its content changes (key order does not matter). The permission is
 * the verb's own — a live invalidation grants nothing the matching read
 * could not retrieve.
 *
 * Semantics and guarantees:
 * - Best-effort, at-most-once delivery: events can be lost or duplicated;
 *   the initial refetch fires AFTER the subscription is live, so a write
 *   racing the subscribe is never missed.
 * - `onInvalidate` runs single-flight with one trailing call: a burst of
 *   events during an in-flight refetch coalesces into exactly one more. With
 *   `minRefetchIntervalMs` it also runs at most once per interval (a trailing
 *   call still follows the last event), so a busy resource cannot spend your
 *   per-minute budget on refetches.
 * - An event says only that the resource changed — not what. Re-read the
 *   visible head of it and merge by id, so edits and deletions show too; a
 *   "newer than X" cursor read would never see them.
 * - The host renews authorization periodically; when it definitively ends
 *   (`status: 'ended'`), `endedReason` says why — `collection_deleted` (the
 *   observed resource is gone) and `authorization_revoked` are terminal,
 *   others may warrant resubscribing (unmount/remount or toggle `enabled`).
 *
 * @example
 * // Any catalog verb of kind 'subscribe' (see the capability catalog for the
 * // entity id and payload); refetch the head of the resource on each event.
 * const { status } = useEntitySubscription(
 *   ENTITY_ID,
 *   { op: 'subscribe_feed' },
 *   refetchHead,
 *   { enabled: member, minRefetchIntervalMs: 2_000 },
 * );
 */
export function useEntitySubscription(
  entityId: string,
  payload: { op: string } & Record<string, unknown>,
  onInvalidate: () => void | Promise<void>,
  options?: { enabled?: boolean; minRefetchIntervalMs?: number },
): EntitySubscriptionState {
  const bridge = useBridge();
  const [state, setState] = useState<EntitySubscriptionState>({ status: 'subscribing' });
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;
  const enabled = options?.enabled ?? true;
  const minRefetchIntervalMs = Math.max(0, options?.minRefetchIntervalMs ?? 0);
  // The payload is wire JSON, so its content is the identity that matters —
  // a fresh object literal each render, or the same fields in another order,
  // must not re-subscribe.
  const payloadKey = stableStringify(payload);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'ended', endedReason: 'unsubscribed' });
      return;
    }
    let cancelled = false;
    let subscriptionId: string | null = null;
    let unsubscribeEvent: (() => void) | null = null;
    let unsubscribeEnded: (() => void) | null = null;

    // Single-flight + exactly one trailing call: events arriving during an
    // in-flight refetch coalesce into one follow-up, never a parallel run.
    // With a minimum interval, a run that would land too soon after the last
    // one is deferred to the interval's end — still exactly one.
    let refetching = false;
    let trailing = false;
    let lastRunAt = -Infinity;
    let intervalTimer: ReturnType<typeof setTimeout> | null = null;
    const runInvalidate = async (): Promise<void> => {
      if (refetching || intervalTimer) {
        trailing = true;
        return;
      }
      const wait = lastRunAt + minRefetchIntervalMs - Date.now();
      if (wait > 0) {
        trailing = true;
        intervalTimer = setTimeout(() => {
          intervalTimer = null;
          if (trailing && !cancelled) {
            trailing = false;
            void runInvalidate();
          }
        }, wait);
        return;
      }
      refetching = true;
      lastRunAt = Date.now();
      try {
        await onInvalidateRef.current();
      } catch {
        // The caller's refetch failed — their concern; the subscription
        // stays live and the next event tries again.
      }
      refetching = false;
      if (trailing && !cancelled) {
        trailing = false;
        void runInvalidate();
      }
    };

    setState({ status: 'subscribing' });
    void (async () => {
      try {
        const res = await bridge.request<{
          output?: { kind?: string; subscriptionId?: string };
        }>('entity_invoke', {
          entityId,
          payload: JSON.parse(payloadKey) as Record<string, unknown>,
        });
        const subscription = res?.output;
        if (
          !subscription ||
          subscription.kind !== 'subscription' ||
          typeof subscription.subscriptionId !== 'string'
        ) {
          if (!cancelled) setState({ status: 'ended', endedReason: 'transport_error' });
          return;
        }
        if (cancelled) {
          bridge.releaseSubscription(subscription.subscriptionId);
          return;
        }
        subscriptionId = subscription.subscriptionId;
        unsubscribeEvent = bridge.onSubscriptionEvent(subscriptionId, () => void runInvalidate());
        unsubscribeEnded = bridge.onSubscriptionEnded(subscriptionId, (reason) => {
          if (!cancelled) setState({ status: 'ended', endedReason: reason });
        });
        setState({ status: 'active' });
        // Subscribe-then-fetch ordering: refetch AFTER the subscription is
        // live so a write racing the handshake is reflected either in this
        // fetch or in the event it triggers — never silently missed.
        void runInvalidate();
      } catch {
        if (!cancelled) setState({ status: 'ended', endedReason: 'transport_error' });
      }
    })();

    return () => {
      cancelled = true;
      if (intervalTimer) clearTimeout(intervalTimer);
      unsubscribeEvent?.();
      unsubscribeEnded?.();
      if (subscriptionId) bridge.releaseSubscription(subscriptionId);
    };
  }, [bridge, entityId, payloadKey, enabled, minRefetchIntervalMs]);

  return state;
}

/**
 * Subscribes to realtime change events for a datastore collection
 * (ADR-012 Phase M4) — `useEntitySubscription` bound to the datastore's
 * `subscribe` verb. Notify-then-refetch: the platform pushes a minimal
 * "this collection changed" invalidation and `onInvalidate` decides what to
 * refetch — typically re-running the `useFiasDataStore().query(...)` your
 * component renders from.
 *
 * Requires the `data:store` permission (same as reading the collection —
 * a live invalidation grants nothing polling couldn't retrieve).
 *
 * Semantics and guarantees are `useEntitySubscription`'s. Scope-aware:
 * shared collections notify on any user's writes; user-scoped collections
 * only on yours; workspace-scoped need `workspaceId` and an active
 * membership.
 *
 * Audiences (ADR-034): in a collection with audiences, changes are announced
 * per audience — pass `audience` (one the user reads; see
 * `useFiasAccess().canReadAudience`), and subscribe once per audience you
 * show. Without one the subscription ends (`AUDIENCE_REQUIRED`).
 *
 * @example
 * const dataStore = useFiasDataStore();
 * const [scores, setScores] = useState<DataStoreDocument[]>([]);
 * const refresh = useCallback(async () => {
 *   const res = await dataStore.query('scores', { limit: 50 });
 *   setScores(res.documents);
 * }, [dataStore]);
 * const { status } = useDataSubscription('scores', refresh);
 */
export function useDataSubscription(
  collection: string,
  onInvalidate: () => void | Promise<void>,
  options?: { workspaceId?: string; audience?: string; enabled?: boolean },
): DataSubscriptionState {
  const audience = options?.audience;
  return useEntitySubscription(
    PLUGIN_DATASTORE_ENTITY_ID,
    {
      op: 'subscribe',
      collection,
      workspaceId: options?.workspaceId,
      ...(audience !== undefined ? { audience } : {}),
    },
    onInvalidate,
    { enabled: options?.enabled },
  );
}

// ============================================================================
// useVaultUserDocuments — CONSENTED access to the user's pre-existing docs
// ============================================================================

/** Metadata of a user document this arche holds a grant for. */
export interface VaultUserDocumentSummary {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  sensitivity: string;
  createdAt: string;
  updatedAt: string;
  /**
   * What the user lets you do with this document: `'read'` (open it) or
   * `'write'` (also save changes to it — see `saveContent`). Open a `'read'`
   * document read-only, and call `requestEditAccess` when the user starts
   * editing. Optional: absent on hosts that predate edit access — treat
   * absent as `'read'`.
   */
  access?: 'read' | 'write';
  /**
   * Opaque token naming the document's current CONTENT. REQUIRED by
   * `saveContent` as `expectedRevision` — it is the user's file and they may
   * be changing it elsewhere. Never parse it.
   */
  revision?: string;
}

/** Result of the host picker: what the user picked and confirmed, or a
 * cancel. A cancel is a normal outcome — never treat it as an error. */
export type VaultUserDocumentPickResult =
  | { canceled: true }
  | {
      documents: Array<
        Pick<VaultUserDocumentSummary, 'documentId' | 'name' | 'mimeType' | 'sizeBytes'> & {
          /** What the user granted for this document. */
          access?: 'read' | 'write';
        }
      >;
    };

/** Input to `useVaultUserDocuments().saveContent`. */
export interface VaultUserDocumentSaveContentParams {
  documentId: string;
  /** The new content, up to 15 MB: a `string` (UTF-8 text — the document must
   *  be a text type) or bytes. Transport is chosen for you. The document's
   *  TYPE cannot change, so there is no `contentType`. */
  content: string | Uint8Array | ArrayBuffer | Blob;
  /** REQUIRED. The `revision` you last saw for this document. */
  expectedRevision: string;
}

export interface VaultUserDocumentsApi {
  /**
   * Open the HOST's Vault picker + consent sheet. The user hand-picks
   * documents and explicitly confirms the grant; your plugin never sees
   * their vault listing — only the picked documents come back. Must be
   * called from a user gesture (a button click), and only works in the
   * published plugin (the preview always resolves `{ canceled: true }`).
   */
  pick(options?: {
    maxDocuments?: number;
    /** Ask for EDIT access: the user is shown a consent that says your app
     *  may CHANGE what they pick. Needs `vault:user-documents:edit` IN
     *  ADDITION TO `vault:user-documents:read` — `:edit` does not include
     *  `:read`, and every method of this hook (this one included) is gated
     *  on `:read`. */
    access?: 'read' | 'write';
    /**
     * The types you can open: MIME types (`'application/pdf'`), MIME
     * families (`'image/*'`) or extensions (`'.docx'`), up to 20. The host
     * offers matching files; the user can still reveal the rest, shown
     * disabled. Omitted: every type.
     */
    accept?: readonly string[];
  }): Promise<VaultUserDocumentPickResult>;
  /**
   * Ask the user to let you EDIT a document you can already open. The host
   * shows its own consent for that one file — no picker — and resolves with
   * the document (now `access: 'write'`), or `{ canceled: true }`. Call it
   * from a user gesture, when the user actually starts editing — not on open,
   * and not again right after a decline. Needs `vault:user-documents:edit`
   * together with `vault:user-documents:read`.
   * If you hold no grant on the document the host shows the ordinary picker
   * instead, so you learn nothing about files you were not given.
   *
   * CHECK THE RESULT — a resolved promise is not "you may now edit
   * `documentId`". Because of that picker fallback the one document that
   * comes back may be a DIFFERENT one the user chose, and the user may have
   * answered from a sheet that offered view-only. Proceed only when
   * `documents[0].documentId === documentId` and `documents[0].access ===
   * 'write'`; treat anything else (including `{ canceled: true }`) as "no".
   */
  requestEditAccess(documentId: string): Promise<VaultUserDocumentPickResult>;
  /**
   * Save changes, IN PLACE, to a document the user has given you EDIT access
   * to — same `documentId`, so nothing attached to it detaches. Text or bytes,
   * up to 15 MB; one method, transport chosen for you.
   *
   * Manifest: `vault:user-documents:edit` AND `vault:user-documents:read`.
   * Declaring `:edit` alone is not enough — it does not include `:read`.
   *
   * It is the USER'S file, which makes this stricter than saving your own:
   * `expectedRevision` is REQUIRED (they may be editing it elsewhere — a stale
   * one rejects `VERSION_CONFLICT`: re-read, merge or ask, retry); the file's
   * TYPE cannot change (`MIME_CHANGE_NOT_ALLOWED`); and the platform keeps
   * earlier versions for them, which count against THEIR storage — a save
   * that would exceed it rejects `STORAGE_QUOTA_EXCEEDED` and nothing is lost.
   *
   * Rejects `EDIT_ACCESS_REQUIRED` when you hold view-only access (call
   * `requestEditAccess`), and `DOCUMENT_NOT_FOUND` once the user removes your
   * access in Vault → Arche Access — which takes effect on your very next save.
   * Handle both. Debounce (a few seconds idle, plus blur and close) and
   * surface `RATE_LIMIT` rather than looping: this lane allows fewer saves per
   * minute than your own documents do. Every save is recorded with your app's
   * name, and the file shows the user that you changed it.
   */
  saveContent(
    params: VaultUserDocumentSaveContentParams,
  ): Promise<VaultUserDocumentSaveContentResult>;
  /** Documents the user has granted this arche (empty until they pick). */
  list(options?: { limit?: number; offset?: number }): Promise<VaultUserDocumentSummary[]>;
  /**
   * Read one granted document. `includeContent: true` returns UTF-8 text
   * for text documents (a binary document rejects with BINARY_DOCUMENT —
   * use getDownloadUrl for those). Rejects with DOCUMENT_NOT_FOUND once
   * the user revokes access in Vault → Arche Access — handle that path.
   */
  get(
    documentId: string,
    options?: { includeContent?: boolean },
  ): Promise<{ document: VaultUserDocumentSummary; content: string | null }>;
  /** 1-hour signed URL for a granted document's bytes (images, PDFs, …).
   *  Usable as an `<img src>`; NOT fetchable from the plugin iframe
   *  (`connect-src 'none'`) — to process binary content, use getBytes. */
  getDownloadUrl(
    documentId: string,
  ): Promise<{ url: string; expiresAt: number; contentType: string | null }>;
  /**
   * Read a granted document's raw bytes (any content type) — the path for
   * binary-processing plugins (PDF editors, image tools). Size-capped
   * server-side (15 MB; larger documents reject with
   * CONSENTED_DOCUMENT_TOO_LARGE naming the cap — ask the user to open the
   * file manually instead).
   * This verb carries its own per-minute budget, tighter than the rest of
   * the family, because it is the only one that can move megabytes: call
   * sequentially and surface a RATE_LIMIT error to the user/assistant
   * verbatim rather than retrying in a loop. Rejects with
   * DOCUMENT_NOT_FOUND once the user revokes access in Vault → Arche Access
   * — unlike a presigned URL from getDownloadUrl, which stays valid for
   * its TTL, every getBytes call re-checks the grant.
   */
  getBytes(
    documentId: string,
  ): Promise<{ document: VaultUserDocumentSummary; bytes: Uint8Array; contentType: string | null }>;
  /**
   * WORKSPACE-APPROVED reads. When the user is acting in an org workspace
   * whose admin (or its Company) has approved your arche, these read that
   * workspace's documents with no per-document pick — every document the
   * acting member can see, at their role, never proprietary. Same
   * permission (`vault:user-documents:read`), same shapes as the methods
   * above. Two refusals to handle: `WORKSPACE_REQUIRED` (the user is in
   * their Personal vault — offer `pick()`) and `ARCHE_NOT_APPROVED` (this
   * workspace has not approved your arche — say so; approval is an admin's
   * act in Workspace Controls, and revocation is immediate).
   */
  workspace: {
    list(options?: {
      limit?: number;
      offset?: number;
      search?: string;
    }): Promise<VaultUserDocumentSummary[]>;
    get(
      documentId: string,
      options?: { includeContent?: boolean },
    ): Promise<{ document: VaultUserDocumentSummary; content: string | null }>;
    getDownloadUrl(
      documentId: string,
    ): Promise<{ url: string; expiresAt: number; contentType: string | null }>;
    getBytes(documentId: string): Promise<{
      document: VaultUserDocumentSummary;
      bytes: Uint8Array;
      contentType: string | null;
    }>;
  };
}

/**
 * Consented access to documents the USER already owns — the only SDK
 * surface that reaches data your plugin did not create. Requires the
 * `vault:user-documents:read` permission. Every access is user-consented
 * (per-document grants created in the host picker, revocable any time in
 * Vault → Arche Access) and audited by the platform.
 *
 * The picker offers the files of the workspace the user is acting in (the
 * host's workspace switcher — Personal, or an org workspace they belong to)
 * and `list()` returns the grants made in it; a document you hold a grant on
 * stays readable by id whichever workspace the user is in, because a grant
 * belongs to the workspace the document lives in. A plugin never names a
 * workspace; the host does. A member's role caps what they can share and
 * read. Proprietary-sensitivity
 * documents are never grantable. For documents your plugin CREATES, use
 * `useVaultDocuments` instead.
 */
export function useVaultUserDocuments(): VaultUserDocumentsApi {
  const bridge = useBridge();

  const pick = useCallback(
    async (options?: {
      maxDocuments?: number;
      access?: 'read' | 'write';
      accept?: readonly string[];
    }): Promise<VaultUserDocumentPickResult> => {
      return bridge.request<VaultUserDocumentPickResult>('vault_documents_pick', {
        maxDocuments: options?.maxDocuments,
        access: options?.access,
        ...(options?.accept ? { accept: [...options.accept] } : {}),
      });
    },
    [bridge],
  );

  const requestEditAccess = useCallback(
    async (documentId: string): Promise<VaultUserDocumentPickResult> => {
      // The same host-mediated op as `pick`, naming one document. The HOST
      // decides whether it may skip the picker (only for a document this arche
      // already holds a grant on) — nothing here can.
      return bridge.request<VaultUserDocumentPickResult>('vault_documents_pick', {
        maxDocuments: 1,
        access: 'write',
        documentId,
      });
    },
    [bridge],
  );

  const saveContent = useCallback(
    async (
      params: VaultUserDocumentSaveContentParams,
    ): Promise<VaultUserDocumentSaveContentResult> => {
      const routed = await routeSaveContent(params.content);
      if (routed.lane === 'body') {
        return invokeEntityOp<VaultUserDocumentSaveContentResult>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'save_consented_document_content',
          {
            documentId: params.documentId,
            content: routed.text,
            expectedRevision: params.expectedRevision,
          },
        );
      }
      // Bytes, or text past the bridge body: the host does the presigned PUT
      // (the iframe's CSP forbids it). `saveTarget: 'user'` selects the
      // consented lane; no `name`, so an older host refuses rather than
      // creating a new document.
      return bridge.request<VaultUserDocumentSaveContentResult>('vault_documents_upload', {
        saveToDocumentId: params.documentId,
        saveTarget: 'user',
        bytes: bytesToBase64(routed.bytes),
        contentKind: routed.contentKind,
        expectedRevision: params.expectedRevision,
      });
    },
    [bridge],
  );

  const list = useCallback(
    async (options?: { limit?: number; offset?: number }): Promise<VaultUserDocumentSummary[]> => {
      const res = await invokeEntityOp<{ documents: VaultUserDocumentSummary[] }>(
        bridge,
        VAULT_USER_DOCUMENTS_ENTITY_ID,
        'list_consented_documents',
        { limit: options?.limit, offset: options?.offset },
      );
      return res.documents;
    },
    [bridge],
  );

  const get = useCallback(
    async (
      documentId: string,
      options?: { includeContent?: boolean },
    ): Promise<{ document: VaultUserDocumentSummary; content: string | null }> => {
      return invokeEntityOp<{ document: VaultUserDocumentSummary; content: string | null }>(
        bridge,
        VAULT_USER_DOCUMENTS_ENTITY_ID,
        'get_consented_document',
        { documentId, includeContent: options?.includeContent },
      );
    },
    [bridge],
  );

  const getDownloadUrl = useCallback(
    async (
      documentId: string,
    ): Promise<{ url: string; expiresAt: number; contentType: string | null }> => {
      const res = await invokeEntityOp<{
        url: string;
        expiresAt: string;
        contentType: string | null;
      }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_consented_document_url', { documentId });
      return {
        url: res.url,
        expiresAt: new Date(res.expiresAt).getTime(),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  const getBytes = useCallback(
    async (
      documentId: string,
    ): Promise<{
      document: VaultUserDocumentSummary;
      bytes: Uint8Array;
      contentType: string | null;
    }> => {
      const res = await invokeEntityOp<{
        document: VaultUserDocumentSummary;
        bytesBase64: string;
        contentType: string | null;
      }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_consented_document_bytes', { documentId });
      return {
        document: res.document,
        bytes: base64ToBytes(res.bytesBase64),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  // Workspace-approved reads (ADR-022 §7): the same four shapes on the
  // acting workspace, admitted by the platform only under an approval.
  const workspace = useMemo(
    () => ({
      list: async (options?: { limit?: number; offset?: number; search?: string }) => {
        const res = await invokeEntityOp<{ documents: VaultUserDocumentSummary[] }>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'list_workspace_documents',
          { limit: options?.limit, offset: options?.offset, search: options?.search },
        );
        return res.documents;
      },
      get: (documentId: string, options?: { includeContent?: boolean }) =>
        invokeEntityOp<{ document: VaultUserDocumentSummary; content: string | null }>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'get_workspace_document',
          { documentId, includeContent: options?.includeContent },
        ),
      getDownloadUrl: async (documentId: string) => {
        const res = await invokeEntityOp<{
          url: string;
          expiresAt: string;
          contentType: string | null;
        }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_workspace_document_url', { documentId });
        return {
          url: res.url,
          expiresAt: new Date(res.expiresAt).getTime(),
          contentType: res.contentType,
        };
      },
      getBytes: async (documentId: string) => {
        const res = await invokeEntityOp<{
          document: VaultUserDocumentSummary;
          bytesBase64: string;
          contentType: string | null;
        }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_workspace_document_bytes', { documentId });
        return {
          document: res.document,
          bytes: base64ToBytes(res.bytesBase64),
          contentType: res.contentType,
        };
      },
    }),
    [bridge],
  );

  return useMemo(
    () => ({
      pick,
      requestEditAccess,
      saveContent,
      list,
      get,
      getDownloadUrl,
      getBytes,
      workspace,
    }),
    [pick, requestEditAccess, saveContent, list, get, getDownloadUrl, getBytes, workspace],
  );
}

/**
 * Refresh a community-asset URL this many ms before it expires. Larger than the
 * Vault buffer relative to the TTL (5 min vs 1 h) because a gallery render can
 * start well before its `<img>` loads finish.
 */
const COMMUNITY_ASSET_URL_REFRESH_BUFFER_MS = 30_000;

/**
 * Module-scoped cache of community-asset signed URLs, keyed by assetId.
 *
 * Same reasoning as the Vault download-URL cache above: a React render is not
 * the right invalidation boundary for something valid for a fixed wall-clock
 * window.
 *
 * Keyed by assetId ALONE, deliberately: this module is loaded inside one
 * plugin iframe, which runs one arche, so there is no second arche to collide
 * with. (The signature itself is still bound to the arche server-side — that
 * boundary does not depend on this cache.)
 *
 * Because signatures last only ~5 minutes, this cache is what stops a gallery
 * from re-minting on every render — while `unpublish` evicts eagerly so a
 * removed asset stops resolving straight away rather than at TTL.
 */
const communityAssetUrlCache = new Map<string, CommunityAssetUrl>();
const communityAssetUrlInflight = new Map<string, Promise<CommunityAssetUrl[]>>();

/**
 * Batch resolver behind `getUrl` / `getUrls`.
 *
 * Serves what it can from cache, requests only the misses, and dedupes
 * concurrent requests for the same asset so a burst of mounting `<img>`
 * components collapses into one round-trip. Ids the server omits (taken down,
 * not yet published, never existed) are simply absent from the result.
 */
async function fetchCommunityAssetUrls(
  bridge: FiasBridge,
  assetIds: string[],
): Promise<CommunityAssetUrl[]> {
  const now = Date.now();
  const hits: CommunityAssetUrl[] = [];
  const misses: string[] = [];

  for (const assetId of assetIds) {
    const cached = communityAssetUrlCache.get(assetId);
    if (cached && now < cached.expiresAt - COMMUNITY_ASSET_URL_REFRESH_BUFFER_MS) {
      hits.push(cached);
    } else {
      misses.push(assetId);
    }
  }
  if (misses.length === 0) return hits;

  // Coalesce with any in-flight request covering the same asset.
  const pending: Array<Promise<CommunityAssetUrl[]>> = [];
  const toRequest: string[] = [];
  for (const assetId of misses) {
    const inflight = communityAssetUrlInflight.get(assetId);
    if (inflight) pending.push(inflight);
    else toRequest.push(assetId);
  }

  // CHUNKED to the server's cap. `get_urls` is `.max(COMMUNITY_ASSET_MAX_URL_BATCH)`
  // server-side and Zod rejects the whole request when it is exceeded — so an
  // un-chunked gallery of 60 images did not render 50 and drop 10, it rendered
  // NONE. Chunking here rather than surfacing the limit keeps the natural usage
  // (one book, every page at once) working, which is the case this hook exists
  // for. Chunks run concurrently; each caches and clears its own ids.
  for (let i = 0; i < toRequest.length; i += COMMUNITY_ASSET_MAX_URL_BATCH) {
    const chunk = toRequest.slice(i, i + COMMUNITY_ASSET_MAX_URL_BATCH);
    const promise = invokeEntityOp<{ urls: CommunityAssetUrl[] }>(
      bridge,
      COMMUNITY_ASSETS_ENTITY_ID,
      'get_urls',
      { assetIds: chunk },
    )
      .then((res) => {
        for (const url of res.urls) {
          communityAssetUrlCache.set(url.assetId, url);
        }
        return res.urls;
      })
      .finally(() => {
        for (const assetId of chunk) {
          communityAssetUrlInflight.delete(assetId);
        }
      });
    for (const assetId of chunk) {
      communityAssetUrlInflight.set(assetId, promise);
    }
    pending.push(promise);
  }

  const settled = await Promise.all(pending);
  const requested = new Set(assetIds);
  const seen = new Set(hits.map((h) => h.assetId));
  for (const batch of settled) {
    for (const url of batch) {
      // A coalesced in-flight request may cover assets we weren't asked about.
      if (!requested.has(url.assetId) || seen.has(url.assetId)) continue;
      seen.add(url.assetId);
      hits.push(url);
    }
  }
  return hits;
}

/**
 * The user's Creative Library (ADR-037): their styles and characters, made in
 * FiasWorks Studio or any arche they allow, owned by them — or, in a team
 * workspace that approved this arche, the workspace's. Requires
 * `vault:creative:read`; `create` / `update` / `remove` / `restore` also need
 * `vault:creative:write`.
 *
 * Generate in a style with `useImageGeneration().generate({ …, creativeStyle:
 * { styleId } })`: the platform sends the style's pictures and words, so the
 * arche never has to read them itself.
 *
 * @example
 *   const library = useCreativeLibrary();
 *   const styles = await library.list({ kind: 'style' });
 *   await images.generate({ entityId, prompt: 'a red fox', creativeStyle: { styleId: styles[0].itemId } });
 */
/** How long a person may keep the Creative Library picker open. */
const CREATIVE_PICKER_TIMEOUT_MS = 30 * 60 * 1000;

export function useCreativeLibrary(): FiasCreativeLibraryApi {
  const bridge = useBridge();
  return useMemo<FiasCreativeLibraryApi>(() => {
    const item = async (op: string, args: Record<string, unknown>) =>
      (await invokeEntityOp<{ item: FiasCreativeItem }>(bridge, VAULT_CREATIVE_ENTITY_ID, op, args))
        .item;
    return {
      list: async (options = {}) =>
        (
          await invokeEntityOp<{ items: FiasCreativeItem[] }>(
            bridge,
            VAULT_CREATIVE_ENTITY_ID,
            'list_items',
            { ...options },
          )
        ).items,
      get: (itemId, version) =>
        item('get_item', { itemId, ...(version !== undefined ? { version } : {}) }),
      create: (fields) => item('create_item', { referenceDocumentIds: [], ...fields }),
      update: (itemId, fields) => item('update_item', { itemId, ...fields }),
      remove: (itemId) => item('delete_item', { itemId }),
      restore: (itemId) => item('restore_item', { itemId }),
      pick: async (options = {}) => {
        let answer:
          | FiasCreativePickResult
          | { error: { code: string; message: string } }
          | undefined;
        try {
          answer = await bridge.request<
            FiasCreativePickResult | { error: { code: string; message: string } }
          >(
            'client_entity_invoke',
            {
              entityId: CREATIVE_PICKER_ENTITY_ID,
              input: options.kind ? { kind: options.kind } : {},
            },
            CREATIVE_PICKER_TIMEOUT_MS,
          );
        } catch (err) {
          // A host without the picker (an older Fias, the dev harness) has no
          // executor for it: the picker is simply not available there.
          if (isClientEntityUnavailable(err)) {
            throw new FiasBridgeError(
              'The Creative Library picker is not available here',
              'PICKER_UNAVAILABLE',
            );
          }
          throw err;
        }
        if (answer && 'error' in answer) {
          throw new FiasBridgeError(answer.error.message, answer.error.code);
        }
        return answer as FiasCreativePickResult;
      },
    };
  }, [bridge]);
}

/** Connecting waits on a person (and maybe a provider sign-in in a popup). */
const CONNECT_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Outside accounts (X, LinkedIn, …) the workspace the app is open in lets it
 * use (ADR-029 §2). Requires `connections:use`.
 *
 * The app never sees a credential. `connect(provider)` asks the host to show
 * its connect screen, where the person connects an account or lets this app
 * use one already connected; the host makes every grant. Acting as an account
 * (posting, reading its audience) is a verb on the provider's own entity.
 *
 * @example
 *   const connections = useFiasConnections();
 *   const accounts = await connections.list();
 *   const x = accounts.find((a) => a.provider === 'x') ?? (await connections.connect('x'));
 */
export function useFiasConnections(): FiasConnectionsApi {
  const bridge = useBridge();

  const list = useCallback(async (): Promise<FiasConnection[]> => {
    const res = await invokeEntityOp<{ connections: FiasConnection[] }>(
      bridge,
      CONNECTIONS_ENTITY_ID,
      'list',
    );
    return res.connections;
  }, [bridge]);

  const connect = useCallback(
    async (provider: string): Promise<FiasConnectResult> => {
      const result = await bridge.request<
        FiasConnectResult | { error: { code: string; message: string } }
      >(
        'client_entity_invoke',
        { entityId: CONNECTIONS_CONNECT_ENTITY_ID, input: { provider } },
        CONNECT_TIMEOUT_MS,
      );
      if (result && 'error' in result) {
        throw new FiasBridgeError(result.error.message, result.error.code);
      }
      return result;
    },
    [bridge],
  );

  const release = useCallback(
    async (connectionId: string): Promise<void> => {
      await invokeEntityOp(bridge, CONNECTIONS_ENTITY_ID, 'release', { connectionId });
    },
    [bridge],
  );

  return useMemo(() => ({ list, connect, release }), [list, connect, release]);
}

/**
 * Pages on websites Fias approved, read as DATA (ADR-038). Requires
 * `web:sources:read`.
 *
 * The app names a page on an approved site and a form — the fields it wants —
 * and gets back values of those kinds (numbers, dates, yes/no, the app's own
 * choices, lists of them), each `null` when the page does not say. It never
 * receives the page's text, not even a short label. One copy of each page serves every app, fetched at most
 * once an hour; a fresh fetch and its reading are charged to the user (well
 * under a cent for a typical page), and a cached result is free. Where a
 * wrong number matters, ask for `reads: 2`: the page is read twice and only
 * what both readings agree on comes back; the rest is null and named in
 * `disagreed`.
 *
 * @example
 *   const web = useFiasWebSources();
 *   const result = await web.extract<{
 *     rates: Array<{ termMonths: number | null; ira: boolean | null; apy: number | null }>;
 *   }>({
 *     url: 'https://www.becu.org/everyday-banking/certificates',
 *     form: {
 *       description: 'Share certificate rates',
 *       fields: {
 *         rates: {
 *           kind: 'list',
 *           item: {
 *             termMonths: { kind: 'integer', min: 1, max: 120, description: 'Term in months' },
 *             ira: { kind: 'boolean', description: 'An IRA certificate' },
 *             apy: { kind: 'number', min: 0, max: 25, description: 'APY in percent' },
 *           },
 *         },
 *       },
 *     },
 *   });
 *   // Show result.site.name and result.fetchedAt beside the figures.
 */
const DISAGREED_ENTRY = /^([^[]+)\[(\d+)\](?:\.(.+))?$/;

/**
 * Splits an extraction's `disagreed` into the field names, the rows lists
 * lost and the cells nulled in kept rows, so an app never parses the entries
 * or does the index arithmetic itself.
 */
export function disagreedEntries(disagreed: readonly string[]): FiasWebSourceDisagreedEntries {
  const out: FiasWebSourceDisagreedEntries = { fields: [], rows: [], cells: [] };
  const cells: Array<{ list: string; index: number; cell: string }> = [];
  for (const entry of disagreed) {
    const match = DISAGREED_ENTRY.exec(entry);
    if (!match) {
      out.fields.push(entry);
    } else if (match[3] === undefined) {
      out.rows.push({ list: match[1], index: Number(match[2]) });
    } else {
      cells.push({ list: match[1], index: Number(match[2]), cell: match[3] });
    }
  }
  for (const cell of cells) {
    const removedBefore = out.rows.filter(
      (row) => row.list === cell.list && row.index < cell.index,
    ).length;
    out.cells.push({ ...cell, position: cell.index - removedBefore });
  }
  return out;
}

/**
 * The field names in an extraction's `disagreed`, without the `name[i]` row
 * entries and the `name[i].cell` cell entries: `disagreedEntries(disagreed).fields`.
 */
export function disagreedFieldNames(disagreed: readonly string[]): string[] {
  return disagreedEntries(disagreed).fields;
}

export function useFiasWebSources(): FiasWebSourcesApi {
  const bridge = useBridge();

  const listSites = useCallback(
    async (options?: FiasWebSourceListOptions): Promise<FiasWebSourceSiteList> =>
      invokeEntityOp<FiasWebSourceSiteList>(bridge, WEB_SOURCES_ENTITY_ID, 'list_sites', {
        ...(options?.category !== undefined ? { category: options.category } : {}),
        ...(options?.search !== undefined ? { search: options.search } : {}),
        ...(options?.limit !== undefined ? { limit: options.limit } : {}),
        ...(options?.offset !== undefined ? { offset: options.offset } : {}),
      }),
    [bridge],
  );

  const extract = useCallback(
    async <T extends FiasWebSourceData = FiasWebSourceData>(
      request: FiasWebSourceExtractRequest,
    ): Promise<FiasWebSourceExtraction<T>> =>
      invokeEntityOp<FiasWebSourceExtraction<T>>(bridge, WEB_SOURCES_ENTITY_ID, 'extract', {
        url: request.url,
        form: request.form,
        ...(request.maxAgeSeconds !== undefined ? { maxAgeSeconds: request.maxAgeSeconds } : {}),
        ...(request.reads !== undefined ? { reads: request.reads } : {}),
      }),
    [bridge],
  );

  return useMemo(() => ({ listSites, extract }), [listSites, extract]);
}

/**
 * X, as an account the workspace this app is open in let it use (ADR-029 §3).
 *
 * Requires `connections:use` in `fias-plugin.json`. Get a `connectionId` from
 * `useFiasConnections()` (`list`, or `connect('x')`). Each call is charged to
 * the user as X prices it: a read by what it returns, a post per post — and a
 * post with a link (any URL or web address) costs more than a plain one. `publishPost` puts a confirm sheet in front of the
 * person — the platform's own words: the account, the workspace, the post —
 * and resolves only after they approve. A decline rejects with
 * `FiasBridgeError` code `USER_DECLINED`. Never available in the dev harness:
 * a post needs a person in the Fias app.
 *
 * @example
 * ```tsx
 * const x = useFiasX();
 * const { posts } = await x.publishPost({ connectionId, posts: [{ text: 'Hello' }] });
 * ```
 */
export function useFiasX(): FiasXApi {
  const bridge = useBridge();
  return useMemo<FiasXApi>(
    () => ({
      publishPost: (params) =>
        invokeEntityOp<FiasXPublishResult>(bridge, X_ENTITY_ID, 'publish_post', { ...params }),
      searchUsers: async (connectionId, query, options) =>
        (
          await invokeEntityOp<{ users: FiasXUser[] }>(bridge, X_ENTITY_ID, 'search_users', {
            connectionId,
            query,
            ...options,
          })
        ).users,
      getUserPosts: async (connectionId, options) =>
        (
          await invokeEntityOp<{ posts: FiasXPost[] }>(bridge, X_ENTITY_ID, 'get_user_posts', {
            connectionId,
            ...options,
          })
        ).posts,
      getPostMetrics: async (connectionId, postIds) =>
        (
          await invokeEntityOp<{ metrics: FiasXPostMetrics[] }>(
            bridge,
            X_ENTITY_ID,
            'get_post_metrics',
            { connectionId, postIds },
          )
        ).metrics,
      getAccountStats: (connectionId) =>
        invokeEntityOp<FiasXAccountStats>(bridge, X_ENTITY_ID, 'get_account_stats', {
          connectionId,
        }),
    }),
    [bridge],
  );
}

/**
 * Schedule one call for later (ADR-029 §5) — e.g. a post at 9 AM tomorrow.
 *
 * Requires `schedule:invoke` in `fias-plugin.json`, plus whatever the call
 * itself needs (`connections:use` to post). For an action outside Fias the
 * person approves the exact call AND its time in a confirm sheet first; a
 * decline rejects with `USER_DECLINED`. When it is due every check runs again
 * — if the account was disconnected or the app lost access, nothing is sent
 * and `list()` shows it as `refused`. There is no edit: cancel and schedule
 * again (a new approval).
 *
 * @example
 * ```tsx
 * const schedules = useFiasSchedules();
 * await schedules.schedule({
 *   entityId: X_ENTITY_ID,
 *   verb: 'publish_post',
 *   payload: { connectionId, posts: [{ text: 'Launch day' }] },
 *   fireAt: tomorrowAtNine,
 * });
 * ```
 */
export function useFiasSchedules(): FiasSchedulesApi {
  const bridge = useBridge();
  return useMemo<FiasSchedulesApi>(
    () => ({
      schedule: (params) =>
        invokeEntityOp<{ scheduleId: string; fireAt: string }>(
          bridge,
          SCHEDULED_INVOCATIONS_ENTITY_ID,
          'schedule',
          {
            entityId: params.entityId,
            verb: params.verb,
            payload: params.payload,
            fireAt: params.fireAt instanceof Date ? params.fireAt.toISOString() : params.fireAt,
          },
        ),
      list: async (options) =>
        (
          await invokeEntityOp<{ schedules: FiasSchedule[] }>(
            bridge,
            SCHEDULED_INVOCATIONS_ENTITY_ID,
            'list',
            {
              ...(options?.limit !== undefined ? { limit: options.limit } : {}),
              ...(options?.scheduleIds ? { scheduleIds: options.scheduleIds } : {}),
            },
          )
        ).schedules,
      cancel: async (scheduleId) => {
        await invokeEntityOp(bridge, SCHEDULED_INVOCATIONS_ENTITY_ID, 'cancel', { scheduleId });
      },
    }),
    [bridge],
  );
}

/**
 * LinkedIn, as a member account the workspace this app is open in let it use
 * (ADR-029 §3). Requires `connections:use`; get a `connectionId` from
 * `useFiasConnections()`. Each call is charged to the user. No post-metrics or
 * audience calls yet: LinkedIn serves them only with analytics scopes the
 * connection does not ask for. `publishPost` puts
 * a confirm sheet in front of the person and resolves only after they approve;
 * a decline rejects with code `USER_DECLINED`.
 */
export function useFiasLinkedIn(): FiasLinkedInApi {
  const bridge = useBridge();
  return useMemo<FiasLinkedInApi>(
    () => ({
      publishPost: (params) =>
        invokeEntityOp<{ postUrn: string; url: string | null }>(
          bridge,
          LINKEDIN_ENTITY_ID,
          'publish_post',
          { ...params },
        ),
      searchOrganizations: async (connectionId, vanityName) =>
        (
          await invokeEntityOp<{ organizations: FiasLinkedInOrganization[] }>(
            bridge,
            LINKEDIN_ENTITY_ID,
            'search_organizations',
            { connectionId, query: vanityName },
          )
        ).organizations,
    }),
    [bridge],
  );
}

/**
 * Send email AS the Company this app is being used in (ADR-023), from the
 * Company's own verified domain — not from Fias.
 *
 * Requires `email:company:send` in `fias-plugin.json`, AND a Company admin
 * authorizing this app to send (they choose who it may email: this workspace,
 * the whole Company, or anyone). The user must be working in one of the
 * Company's workspaces with edit access; each recipient is charged to them.
 * The From address is always the Company's — you only choose recipients and
 * content.
 *
 * Name members by their ref (`useFiasUser().ref`, or refs you stored); you
 * never see their addresses. Every email carries
 * a one-click unsubscribe, and people who used it are skipped (`muted`).
 *
 * @example
 * ```tsx
 * const email = useCompanyEmail();
 * const result = await email.send({
 *   requestKey: `digest-${weekId}`,
 *   to: assigneeRefs.map((ref) => ({ ref })),
 *   subject: 'Your weekly digest',
 *   text: digestText,
 *   html: digestHtml,
 * });
 * ```
 */
export function useCompanyEmail(): CompanyEmailApi {
  const bridge = useBridge();
  const send = useCallback(
    async (params: CompanyEmailSendParams): Promise<CompanyEmailSendResult> => {
      // Single-verb entity: the payload carries no `op` (see entity-ops.ts).
      const res = await bridge.request<{ output: CompanyEmailSendResult }>('entity_invoke', {
        entityId: COMPANY_EMAIL_SEND_ENTITY_ID,
        payload: params,
      });
      return res.output;
    },
    [bridge],
  );
  return useMemo(() => ({ send }), [send]);
}

/**
 * Publish images to this arche's community, and browse what others published.
 *
 * The platform's only cross-user read path: an image published here is visible
 * to every authenticated user of THIS arche (and no other), after it clears
 * moderation. Publishing is free.
 *
 * Two things to design around:
 *
 *   1. **Publishing is not instant.** `publish` returns
 *      `status: 'pending_moderation'`; the asset appears in `listCommunity`
 *      only once it is approved. Show the author their own pending item via
 *      `listMine` rather than optimistically adding it to the community feed.
 *   2. **Store structure in the Data Store, not here.** Which asset belongs on
 *      which page/level goes in a `shared`-scope collection holding `assetId`s.
 *      Community Assets holds the bytes and nothing queryable.
 *
 * Permissions: `assets:community:read` for browsing and viewing;
 * `assets:community:publish` additionally for publish / unpublish / listMine.
 * A plugin that only DISPLAYS community content should declare only the former.
 *
 * @returns Community assets API.
 */
export function useCommunityAssets(): CommunityAssetsApi {
  const bridge = useBridge();

  const publish = useCallback(
    async (input: CommunityAssetPublishInput): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'publish',
        {
          fileId: input.fileId,
          title: input.title,
          caption: input.caption,
          tags: input.tags,
          clientRef: input.clientRef,
          attributionName: input.attributionName,
        },
      );
      return res.asset;
    },
    [bridge],
  );

  const publishMany = useCallback(
    async (items: CommunityAssetPublishInput[]): Promise<CommunityAssetPublishResult[]> => {
      // Chunked to the server's cap for the same reason as `getUrls` — over the
      // limit, Zod rejects the entire call, so "publish this 30-page book"
      // published nothing rather than the first 20.
      //
      // SEQUENTIAL, unlike the URL chunks: each publish normalizes an image,
      // writes a row and stages bytes to S3, and firing several chunks at once
      // would push a burst through the per-(user, arche) quota checks and turn
      // a partial success into a wall of QUOTA_EXCEEDED entries. Items already
      // fail independently server-side, so results stay per-item either way.
      const results: CommunityAssetPublishResult[] = [];
      for (let i = 0; i < items.length; i += COMMUNITY_ASSET_MAX_PUBLISH_BATCH) {
        const res = await invokeEntityOp<{ results: CommunityAssetPublishResult[] }>(
          bridge,
          COMMUNITY_ASSETS_ENTITY_ID,
          'publish_many',
          { items: items.slice(i, i + COMMUNITY_ASSET_MAX_PUBLISH_BATCH) },
        );
        results.push(...res.results);
      }
      return results;
    },
    [bridge],
  );

  const unpublish = useCallback(
    async (assetId: string): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'unpublish',
        { assetId },
      );
      // Evict eagerly so a removed asset stops resolving from cache now, rather
      // than up to the signature TTL later.
      communityAssetUrlCache.delete(assetId);
      return res.asset;
    },
    [bridge],
  );

  const listMine = useCallback(
    async (options?: CommunityAssetListOptions): Promise<CommunityAssetListResult> =>
      invokeEntityOp<CommunityAssetListResult>(bridge, COMMUNITY_ASSETS_ENTITY_ID, 'list_mine', {
        limit: options?.limit,
        cursor: options?.cursor,
      }),
    [bridge],
  );

  const listCommunity = useCallback(
    async (options?: CommunityAssetListOptions): Promise<CommunityAssetListResult> =>
      invokeEntityOp<CommunityAssetListResult>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'list_community',
        { limit: options?.limit, cursor: options?.cursor, tag: options?.tag },
      ),
    [bridge],
  );

  const get = useCallback(
    async (assetId: string): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'get',
        { assetId },
      );
      return res.asset;
    },
    [bridge],
  );

  const getUrls = useCallback(
    (assetIds: string[]): Promise<CommunityAssetUrl[]> => fetchCommunityAssetUrls(bridge, assetIds),
    [bridge],
  );

  const getUrl = useCallback(
    async (assetId: string): Promise<CommunityAssetUrl> => {
      const [url] = await fetchCommunityAssetUrls(bridge, [assetId]);
      if (!url) {
        throw new Error(`Community asset ${assetId} is not available`);
      }
      return url;
    },
    [bridge],
  );

  const report = useCallback(
    async (
      assetId: string,
      reason: CommunityAssetReportReason,
      details?: string,
    ): Promise<{ reportId: string; status: string }> =>
      invokeEntityOp<{ reportId: string; status: string }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'report',
        { assetId, reason, details },
      ),
    [bridge],
  );

  return useMemo(
    () => ({
      publish,
      publishMany,
      unpublish,
      listMine,
      listCommunity,
      get,
      getUrl,
      getUrls,
      report,
    }),
    [publish, publishMany, unpublish, listMine, listCommunity, get, getUrl, getUrls, report],
  );
}

// =============================================================================
// Fias AI actions (ui_action client plane)
// =============================================================================

/** Configuration for {@link useFiasAIActions}. */
export interface FiasAIActionsConfig extends FiasAIActionsDeclaration {
  /**
   * The state snapshot the assistant sees, and `availableWhen` filters
   * against. Keep it SMALL and flag-shaped (`{documentOpen: true}` — publish
   * `hasPages: true`, not a page array): accepted snapshots are embedded in
   * the assistant's system prompt every turn, and the host drops oversized
   * ones outright.
   */
  state?: Record<string, unknown>;
  /**
   * Perform a dispatched action. Throw/reject to report failure — the
   * assistant AWAITS this before telling the model the action applied, so a
   * batch ("do X to each file") stops on the failing step instead of
   * marching on. ALWAYS re-check preconditions here: `availableWhen`
   * filtering is presentation, and a stale turn can dispatch a hidden verb.
   */
  onAction: (actionId: string, params: Record<string, unknown>) => void | Promise<unknown>;
}

/**
 * Receive a document the platform hands your app to open (Arche Handoff).
 *
 * Fires when the user says "open this in <your app>" — from the Fias AI
 * assistant, from an email attachment, from any "open in" affordance on a
 * Vault document. Your handler runs once per handoff; a handoff that arrives
 * before this hook mounts is buffered and delivered on registration, so the
 * first one of a session is not lost.
 *
 * ```tsx
 * useArcheHandoff((payload) => {
 *   if (payload.kind !== 'open_document') return;
 *   void openDocumentAsUnsavedCopy(payload.documentId);
 * });
 * ```
 *
 * You get a document REFERENCE, not bytes. Read it with
 * `useVaultUserDocuments().getBytes(documentId)`: the host grants you that one
 * document as it relays the handoff, so no `pick()` is needed first. The grant
 * is per-document and revocable in Vault → Arche Access exactly like a picked
 * one, and it needs `vault:user-documents:read` — declare it, or the grant is
 * refused and the file never arrives.
 *
 * Open it as an UNSAVED COPY: a later save must create a new document, never
 * mutate the source. And you are the authoritative type gate — if you cannot
 * open this file, tell the user plainly rather than opening something broken.
 *
 * To be offered as a destination at all, declare what you open in
 * `fias-plugin.json` (`"fileIntake": { "kinds": ["document"], "types": [".docx"] }`;
 * `types` narrows a kind to the files you really open) and have Fias approve
 * it; declaring this hook alone does not enrol you. A kind or type added later
 * stays off until it is approved too.
 */
export function useArcheHandoff(onHandoff: (payload: ArcheHandoffPayload) => void): void {
  const bridge = useBridge();
  // Read through a ref so a new closure each render doesn't churn the
  // registration (and re-drain the buffered handoff).
  const handlerRef = useRef(onHandoff);
  handlerRef.current = onHandoff;

  useEffect(() => {
    const handler = (payload: ArcheHandoffPayload) => handlerRef.current(payload);
    bridge.setHandoffHandler(handler);
    return () => bridge.clearHandoffHandler(handler);
  }, [bridge]);
}

// ---------------------------------------------------------------------------
// Document Sign — reading back an envelope your app started
// ---------------------------------------------------------------------------

/** One recipient of an envelope your app started.
 *
 *  No email address, deliberately. A recipient's contact details are a third
 *  party's, and name + role + state is what an approval-status UI needs. If
 *  your app supplied the recipients, you already have their addresses. */
export interface SignEnvelopeRecipient {
  recipientId: string;
  name: string | null;
  role: 'signer' | 'viewer' | 'approver';
  status:
    | 'pending'
    | 'notified'
    | 'viewed'
    | 'signed'
    | 'declined'
    | 'declined_electronic'
    | 'expired';
  /** Position in a sequential signing order; 0-based. Meaningless for parallel. */
  routingOrder: number;
  signedAt: string | null;
  declinedAt: string | null;
}

/** An envelope your app started, as your app may see it. Never its documents. */
export interface SignEnvelope {
  envelopeId: string;
  title: string;
  status: 'draft' | 'sent' | 'in_progress' | 'completed' | 'declined' | 'voided' | 'expired';
  signingOrder: 'parallel' | 'sequential';
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  declinedAt: string | null;
  expiresAt: string | null;
  recipients: SignEnvelopeRecipient[];
}

/** One entry of an envelope's audit timeline. A projection, not the stored
 *  row: signer IP addresses, user agents and the hash-chain columns stay
 *  server-side. */
export interface SignEnvelopeEvent {
  /** e.g. `EnvelopeSent`, `RecipientViewed`, `RecipientSigned`, `EnvelopeCompleted`. */
  type: string;
  actorKind: 'owner' | 'recipient' | 'system';
  /** Which recipient the event is about, when it is about one. */
  recipientId: string | null;
  createdAt: string;
}

export interface SignEnvelopeEventsPage {
  events: SignEnvelopeEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentSignApi {
  /**
   * Read the current state of one envelope your app started — status, who the
   * recipients are, and where each of them has got to.
   *
   * Rejects with `ENVELOPE_NOT_FOUND_OR_NOT_GRANTED` for an envelope your app
   * did not start, one on a workspace the user cannot act for, one that does
   * not exist, one that is still a DRAFT, AND one whose access the user has
   * since removed. Those are deliberately the same error: a distinguishable
   * "it exists but you cannot see it" would let anyone probe for envelope
   * ids. Handle it as "we can no longer follow this", not as a bug.
   *
   * A draft is not readable until the user presses Send — that is the moment
   * they grant access. A `saved_draft` handoff result gives you an id to keep;
   * reading it succeeds once the envelope is sent, not before.
   */
  getEnvelope(envelopeId: string): Promise<SignEnvelope>;
  /**
   * Read the envelope's audit timeline, oldest first. Same access rules and
   * the same single error as `getEnvelope` — authorization is re-checked per
   * call, so a revocation between two calls stops the second one.
   */
  listEvents(
    envelopeId: string,
    options?: { limit?: number; offset?: number },
  ): Promise<SignEnvelopeEventsPage>;
}

/**
 * Follow the envelopes your app handed to Document Sign.
 *
 * Requires the `docsign:envelopes:read` permission, and reaches ONLY envelopes
 * your app itself started via a `sign_document` handoff — never one the user
 * created in Document Sign directly, never another app's, and never any
 * envelope's document bytes. Two independent gates, both of which the user
 * controls: they grant access by pressing Send on an envelope you handed over,
 * and they remove it in Vault → Arche Access.
 *
 * There is no list verb. Store the `envelopeId` from the
 * `sign_document_result` handoff against your own record and read by id —
 * which is also why that result must be matched to an outstanding `requestId`
 * and consumed once.
 *
 * **This is where truth comes from.** The handoff result is a POINTER: it
 * rides navigation state, which is same-origin forgeable, so it tells you
 * which envelope to look at and nothing more. Write "signed" into an approval
 * trail only on the strength of a read.
 *
 * There are no webhooks. Poll when your UI needs to know — on mount, on a
 * refresh the user asked for — not on a timer.
 *
 * ```tsx
 * const sign = useDocumentSign();
 * const envelope = await sign.getEnvelope(approval.envelopeId);
 * if (envelope.status === 'completed') markApproved(approval.id);
 * ```
 */
export function useDocumentSign(): DocumentSignApi {
  const bridge = useBridge();

  const getEnvelope = useCallback(
    async (envelopeId: string): Promise<SignEnvelope> => {
      const res = await invokeEntityOp<{ envelope: SignEnvelope }>(
        bridge,
        DOCUMENT_SIGN_ENTITY_ID,
        'get_consented_envelope',
        { envelopeId },
      );
      return res.envelope;
    },
    [bridge],
  );

  const listEvents = useCallback(
    async (
      envelopeId: string,
      options?: { limit?: number; offset?: number },
    ): Promise<SignEnvelopeEventsPage> => {
      return invokeEntityOp<SignEnvelopeEventsPage>(
        bridge,
        DOCUMENT_SIGN_ENTITY_ID,
        'list_consented_envelope_events',
        { envelopeId, limit: options?.limit, offset: options?.offset },
      );
    },
    [bridge],
  );

  return useMemo(() => ({ getEnvelope, listEvents }), [getEnvelope, listEvents]);
}

/**
 * Declare Fias AI actions at RUNTIME — the dynamic half of the plugin
 * AI-action plane (`ai:actions` permission for the declaration; TRUSTED arches only — the host
 * silently ignores runtime declarations from non-trusted arches, whose lane
 * is the manifest `fiasAI` block, fixed at publish and reviewed with the
 * submission). Both lanes merge host-side; the manifest wins id collisions.
 *
 * Pass `null` to declare nothing (e.g. while loading). The declaration and
 * the state snapshot are each re-sent only when their content changes; the
 * host additionally coalesces rapid re-sends trailing-edge (latest wins,
 * applied within ~1s / ~250ms for state) — nothing is lost, but drive
 * declaration changes off real capability changes, not render churn.
 *
 * Returns `{ openAssistant }` — a fire-and-forget request that the host open
 * the global Fias AI panel, for CONTEXTUAL affordances (an empty-state "Ask
 * Fias AI" button; the host also shows its own "Works with Fias AI" chip).
 * Opening needs NO permission — any plugin may do it, actions or not; the
 * user can still use the assistant's own tools — and `useFiasChrome` offers
 * the same call. The host debounces it and honours the user's last dismissal
 * — never loop or retry, and there is deliberately no way to pass text along.
 */
export function useFiasAIActions(config: FiasAIActionsConfig | null): {
  openAssistant: () => void;
} {
  const bridge = useBridge();

  // The handler is registered once per bridge and reads the latest onAction
  // through a ref — a re-render must not tear down dispatch handling.
  const onActionRef = useRef<FiasAIActionsConfig['onAction'] | null>(config?.onAction ?? null);
  onActionRef.current = config?.onAction ?? null;

  useEffect(() => {
    bridge.setAiActionHandler((actionId, params) => {
      const handler = onActionRef.current;
      if (!handler) {
        throw new Error('no Fias AI action handler is currently registered');
      }
      return handler(actionId, params);
    });
    return () => bridge.setAiActionHandler(null);
  }, [bridge]);

  // Declaration — content-diffed via JSON so callers may pass fresh object
  // literals each render without re-sending (the host throttles this lane).
  const declarationJson = config
    ? JSON.stringify({ description: config.description, actions: config.actions })
    : null;
  useEffect(() => {
    let cancelled = false;
    void bridge.waitForInit().then(() => {
      if (cancelled) return;
      bridge.declareAiActions(
        declarationJson ? (JSON.parse(declarationJson) as FiasAIActionsDeclaration) : null,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [bridge, declarationJson]);

  // Clear the declaration when the consumer unmounts — the assistant must
  // not keep offering verbs nothing will handle.
  useEffect(() => {
    return () => bridge.declareAiActions(null);
  }, [bridge]);

  // State snapshot — content-diffed like the declaration.
  const stateJson = config?.state ? JSON.stringify(config.state) : null;
  useEffect(() => {
    if (stateJson === null) return;
    let cancelled = false;
    void bridge.waitForInit().then(() => {
      if (!cancelled) bridge.sendAiState(JSON.parse(stateJson) as Record<string, unknown>);
    });
    return () => {
      cancelled = true;
    };
  }, [bridge, stateJson]);

  const openAssistant = useCallback(() => bridge.openAssistant(), [bridge]);
  return useMemo(() => ({ openAssistant }), [openAssistant]);
}
